import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Paper, Typography, Box, Button, TextField, Tabs, Tab, Snackbar,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Chip, FormControlLabel, Checkbox, Switch, Collapse, Divider, Slider,
  Menu, ListItemIcon, ListItemText
} from '@material-ui/core';
import {
  Add, Save as SaveIcon, Delete, Edit as EditIcon,
  Visibility, VisibilityOff, ExpandMore, ExpandLess,
  Streetview, Search, LocationSearching, Undo,
  CallMerge as MergeIcon, GroupWork as GroupIcon, LinkOff as UngroupIcon,
  Layers as LayersIcon, FileCopy as ImportIcon, CloudUpload as UploadIcon,
  Image as ImageIcon, Lock as LockIcon, LockOpen as LockOpenIcon
} from '@material-ui/icons';
import Alert from '@material-ui/lab/Alert';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import 'leaflet-toolbar';
import 'leaflet-distortableimage';
import 'leaflet-distortableimage/dist/leaflet.distortableimage.css';
import { unionPolygons } from './polygonUnion';

import PlacementQueue from './PlacementQueue';
import SiteTakeoffSummary from './SiteTakeoffSummary';
import LaborEstimate from './LaborEstimate';
import ProductionRateEditor from './ProductionRateEditor';
import {
  AREA_TYPES, AREA_TYPE_LABELS, AREA_TYPE_COLORS,
  PLANT_BEARING_TYPES, SHRUB_DENSITY_OPTIONS,
  MODIFIER_DEFS, DEFAULT_MODIFIERS, DEFAULT_PLANT_DENSITY,
  AREA_CATEGORIES, getAreaTypeLabel, getAreaTypeColor, resolveAreaType,
  SERVICES, SERVICE_LABELS, getSuggestedServices, DEFAULT_LINE_WIDTH,
  TREE_TYPES, DEFAULT_TREE_DETAILS, TREE_CONDITION_OPTIONS, COMMON_TREE_SPECIES,
  PLACEMENT_ITEM_TYPES
} from './siteTakeoffConstants';
import {
  loadEsriLeaflet, checkYearService, enhancedGetPolygonArea,
  getGeometryFromLayer, defaultStyle,
  googleMapsProvider, googleSatelliteProvider, googleHybridProvider,
} from '../MapWithDrawing/Measure/mapUtils';

// Disable all keyboard shortcuts from leaflet-distortableimage —
// they conflict with sidebar text inputs and cause accidental mode changes (e.g. 'd' for drag, 'u' for unlock)
if (L.DistortableImage) L.DistortableImage.action_map = {};
if (L.distortableImage) L.distortableImage.group_action_map = {};

// Calculate polyline centerline length in feet from array of {lat, lng}
const calculateLineLengthFeet = (paths) => {
  if (!paths || paths.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < paths.length; i++) {
    const p1 = L.latLng(paths[i - 1].lat, paths[i - 1].lng);
    const p2 = L.latLng(paths[i].lat, paths[i].lng);
    totalMeters += p1.distanceTo(p2);
  }
  return totalMeters * 3.28084; // meters to feet
};

// ===== STYLES =====
const useStyles = makeStyles((theme) => ({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: theme.spacing(1),
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  contentRow: {
    display: 'flex',
    flex: 1,
    gap: theme.spacing(1),
    overflow: 'hidden',
  },
  mapContainer: {
    flex: 1,
    position: 'relative',
    borderRadius: 4,
    overflow: 'hidden',
    border: '1px solid #ddd',
  },
  sidebar: {
    width: 480,
    minWidth: 480,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    borderRadius: 4,
    border: '1px solid #ddd',
    backgroundColor: '#fff',
  },
  sidebarScroll: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  siteSelector: {
    padding: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
  paddedField: {
    padding: '4px',
    margin: '4px 0',
  },
  siteInfo: {
    padding: '8px',
    margin: '8px 0',
    backgroundColor: '#f8f8f8',
    borderRadius: 4,
  },
  areaListItem: {
    display: 'flex',
    alignItems: 'center',
    padding: '6px 8px',
    borderRadius: 4,
    border: '1px solid #e0e0e0',
    marginBottom: 4,
    cursor: 'pointer',
    transition: 'all 0.15s',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.03)' },
  },
  areaListItemSelected: {
    border: '2px solid #1976d2',
    backgroundColor: 'rgba(25,118,210,0.04)',
  },
  colorDot: {
    width: 14,
    height: 14,
    borderRadius: '50%',
    marginRight: 8,
    border: '1px solid rgba(0,0,0,0.15)',
    flexShrink: 0,
  },
  areaItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  areaItemName: {
    fontSize: '0.82rem',
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  areaItemMeta: {
    fontSize: '0.7rem',
    color: '#888',
  },
  editorSection: {
    padding: theme.spacing(1),
    backgroundColor: '#fafafa',
    borderTop: '2px solid #1976d2',
    marginTop: 8,
  },
  modifierRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '2px 0',
  },
  modifierLabel: {
    fontSize: '0.78rem',
    color: '#555',
  },
  drawingBanner: {
    position: 'absolute',
    top: 8,
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
    padding: '6px 16px',
    backgroundColor: '#1976d2',
    color: '#fff',
    borderRadius: 20,
    fontSize: '0.85rem',
    fontWeight: 500,
    pointerEvents: 'none',
  },
  serviceChip: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
  },
  serviceChipActive: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
    backgroundColor: '#e3f2fd',
    border: '1px solid #1976d2',
    fontWeight: 500,
  },
  serviceChipSuggested: {
    margin: 2,
    height: 26,
    fontSize: '0.72rem',
    backgroundColor: '#e8f5e9',
    border: '1px solid #4caf50',
  },
  servicesWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0,
    padding: '4px 0',
  },
  editorTabs: {
    minHeight: 36,
    backgroundColor: '#fff',
    borderBottom: '1px solid #ddd',
    marginBottom: 4,
    '& .MuiTab-root': { minHeight: 36, fontSize: '0.78rem', padding: '6px 10px', fontWeight: 500 },
    '& .Mui-selected': { color: '#1976d2', fontWeight: 600 },
  },
  saveBar: {
    padding: theme.spacing(1),
    borderTop: '1px solid #eee',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

const OVERLAY_CATEGORIES = [
  { value: '', label: 'None' },
  { value: 'aerial', label: 'Aerial Image' },
  { value: 'drawing', label: 'Drawing' },
  { value: 'plans', label: 'Plans' },
];

const OVERLAY_SUB_TYPES = {
  aerial: [
    { value: '', label: 'None' },
    { value: '2025_satellite', label: '2025 Satellite' },
    { value: '2024_satellite', label: '2024 Satellite' },
    { value: '2023_satellite', label: '2023 Satellite' },
    { value: 'drone', label: 'Drone' },
  ],
  drawing: [
    { value: '', label: 'None' },
    { value: 'sketch', label: 'Sketch' },
    { value: 'markup', label: 'Markup' },
  ],
  plans: [
    { value: '', label: 'None' },
    { value: 'landscape', label: 'Landscape' },
    { value: 'irrigation', label: 'Irrigation' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'tree_inventory', label: 'Tree Inventory' },
    { value: 'demo', label: 'Demo' },
    { value: 'grading', label: 'Grading' },
    { value: 'planting', label: 'Planting' },
    { value: 'hardscape', label: 'Hardscape' },
  ],
};

// ===== COMPONENT =====
const SiteTakeoffPage = (props) => {
  const classes = useStyles();
  const history = useHistory();
  const { jobsiteId } = useParams();
  const { url, user, clients } = props;

  // ----- Site / Client state -----
  const [selectedClient, setSelectedClient] = useState({});
  const [availableSites, setAvailableSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState({});
  const [jobSite, setJobSite] = useState(null);

  // ----- Map state -----
  const [providers, setProviders] = useState([]);
  const [currentProvider, setCurrentProvider] = useState(null);
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const drawnItemsRef = useRef(null);
  const drawControlRef = useRef(null);

  // ----- GIS overlay layers (toggleable, additive on top of base layer) -----
  const gisOverlayLayersRef = useRef({}); // key -> leaflet layer
  const [activeGisOverlays, setActiveGisOverlays] = useState([]);

  // ----- Areas state -----
  const [areas, setAreas] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [showMeasurements, setShowMeasurements] = useState(false);
  const measurementLayerRef = useRef(null);
  const areasRef = useRef(areas);
  const layerMapRef = useRef({}); // areaId -> leaflet layer

  // ----- Property lines state -----
  const [propertyLines, setPropertyLines] = useState([]);
  const [showPropertyLines, setShowPropertyLines] = useState(true);
  const propertyLinesRef = useRef(null); // L.FeatureGroup for property line layers

  // ----- Rate overrides -----
  const [rateOverrides, setRateOverrides] = useState({});

  // ----- Takeoff state -----
  const [takeoffs, setTakeoffs] = useState([]);
  const [activeTakeoffId, setActiveTakeoffId] = useState(null);
  const [takeoffName, setTakeoffName] = useState('');
  const [hasLegacyAreas, setHasLegacyAreas] = useState(false); // JobSite.areas exist but no takeoffs
  const [placementQueue, setPlacementQueue] = useState([]);
  const [plantSizes, setPlantSizes] = useState([]);
  const [placingItemId, setPlacingItemId] = useState(null); // queueId of item being placed
  const [savedOverlayUrls, setSavedOverlayUrls] = useState([]); // previously uploaded overlay URLs for this jobsite
  const [overlayMenuAnchor, setOverlayMenuAnchor] = useState(null);
  const fileInputRef = useRef(null);

  // ----- Area visibility -----
  const [areasVisible, setAreasVisible] = useState(true);

  // ----- Plan overlay state (multiple overlays) -----
  // Each entry: { id, imageUrl, visible, opacity, category, subType }
  const [overlays, setOverlays] = useState([]);
  const [activeOverlayId, setActiveOverlayId] = useState(null);
  const overlayLayersRef = useRef({}); // id -> L.distortableImageOverlay layer

  // ----- UI state -----
  const [activeTab, setActiveTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [saving, setSaving] = useState(false);
  const [modifiersExpanded, setModifiersExpanded] = useState(false);
  const [editorTab, setEditorTab] = useState(0);
  const [editingAreaId, setEditingAreaId] = useState(null);
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelection, setMergeSelection] = useState([]); // array of area IDs selected for merge
  const [groupMode, setGroupMode] = useState(false);
  const [groupSelection, setGroupSelection] = useState([]); // area IDs selected for grouping
  const selectAreaRef = useRef(null); // ref to handleSelectArea for use in map click handlers

  useEffect(() => { areasRef.current = areas; }, [areas]);

  // Fetch plant sizes for placement queue dropdowns
  useEffect(() => {
    if (!url) return;
    axios.get(`${url}/api/customer/plant-sizes`).then(resp => {
      if (resp.data?.success && resp.data?.sizes) setPlantSizes(resp.data.sizes);
    }).catch(() => {});
  }, [url]);

  const selectedArea = useMemo(() => {
    return areas.find(a => (a._id || a.id) === selectedAreaId) || null;
  }, [areas, selectedAreaId]);

  // =============================================
  // MAP INITIALIZATION
  // =============================================
  useEffect(() => {
    if (!mapContainerRef.current || mapInstanceRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [33.505, -112.15],
      zoom: 15,
      maxZoom: 28,
      zoomControl: true,
    });

    // Default tile layer
    L.tileLayer(googleHybridProvider.url, {
      attribution: googleHybridProvider.attribution,
      maxNativeZoom: 20,
      maxZoom: 28,
      errorTileUrl: '',
    }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const measurementGroup = new L.FeatureGroup();
    measurementLayerRef.current = measurementGroup;

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      draw: {
        polygon: {
          allowIntersection: false,
          showArea: true,
          shapeOptions: { ...defaultStyle, fillOpacity: 0.3 },
        },
        rectangle: {
          shapeOptions: { ...defaultStyle, fillOpacity: 0.3 },
        },
        polyline: {
          shapeOptions: { color: '#1976d2', weight: 4, opacity: 0.8 },
        },
        circle: {
          shapeOptions: { color: '#9370DB', fillColor: '#9370DB', fillOpacity: 0.25, weight: 2 },
        },
        circlemarker: false,
        marker: true,
      },
      edit: false,
    });
    map.addControl(drawControl);

    // Handle new shape drawn
    map.on(L.Draw.Event.CREATED, (e) => {
      const layer = e.layer;
      drawnItems.addLayer(layer);

      const isPolyline = (layer instanceof L.Polyline) && !(layer instanceof L.Polygon);
      const isCircle = (layer instanceof L.Circle);
      const isMarker = (layer instanceof L.Marker);

      let paths, newArea;
      const newId = uuidv4();

      if (isMarker) {
        // --- POINT MARKER ---
        const ll = layer.getLatLng();
        paths = [{ lat: ll.lat, lng: ll.lng }];
        const defaultType = AREA_TYPES.TREE_REMAIN;
        const color = getAreaTypeColor(defaultType);

        newArea = {
          id: newId,
          geometryType: 'point',
          name: '',
          areaType: defaultType,
          paths,
          area: '0',
          areaAcres: '0',
          perimeter: '0',
          fillColor: color,
          strokeColor: '#000000',
          treeDetails: { ...DEFAULT_TREE_DETAILS },
          plantDensity: { ...DEFAULT_PLANT_DENSITY, treeCount: 1 },
          modifiers: { ...DEFAULT_MODIFIERS },
          servicesRequired: getSuggestedServices(defaultType),
          notes: '',
          isNew: true,
        };

        // Style marker with colored circle icon
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
          iconSize: [18, 18],
          iconAnchor: [9, 9],
        }));

      } else if (isCircle) {
        // --- ELLIPSE (from circle tool) ---
        const center = layer.getLatLng();
        const radiusMeters = layer.getRadius();
        const radiusFeet = radiusMeters * 3.28084;
        paths = [{ lat: center.lat, lng: center.lng }];
        const areaSF = Math.PI * radiusFeet * radiusFeet;
        const perimeterFeet = 2 * Math.PI * radiusFeet;
        const defaultType = AREA_TYPES.ELLIPSE_ZONE;
        const color = getAreaTypeColor(defaultType);

        newArea = {
          id: newId,
          geometryType: 'ellipse',
          name: '',
          areaType: defaultType,
          paths,
          radiusX: parseFloat(radiusFeet.toFixed(2)),
          radiusY: parseFloat(radiusFeet.toFixed(2)),
          area: areaSF.toFixed(2),
          areaAcres: (areaSF / 43560).toFixed(4),
          perimeter: perimeterFeet.toFixed(2),
          fillColor: color,
          strokeColor: '#000000',
          plantDensity: { ...DEFAULT_PLANT_DENSITY },
          modifiers: { ...DEFAULT_MODIFIERS },
          servicesRequired: getSuggestedServices(defaultType),
          notes: '',
          isNew: true,
        };

        layer.setStyle({
          fillColor: color,
          fillOpacity: 0.25,
          color,
          weight: 2,
        });

      } else if (isPolyline) {
        // --- POLYLINE ---
        const latlngs = layer.getLatLngs();
        paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const lengthFeet = calculateLineLengthFeet(paths);
        const defaultType = AREA_TYPES.TR_DG;
        const widthFt = DEFAULT_LINE_WIDTH;
        const areaSF = lengthFeet * widthFt;

        newArea = {
          id: newId,
          geometryType: 'polyline',
          name: '',
          areaType: defaultType,
          paths,
          length: lengthFeet.toFixed(2),
          width: widthFt,
          area: areaSF.toFixed(2),
          areaAcres: (areaSF / 43560).toFixed(4),
          perimeter: '0',
          fillColor: getAreaTypeColor(defaultType),
          strokeColor: '#000000',
          plantDensity: { ...DEFAULT_PLANT_DENSITY },
          modifiers: { ...DEFAULT_MODIFIERS },
          servicesRequired: getSuggestedServices(defaultType),
          notes: '',
          isNew: true,
        };

        layer.setStyle({
          color: newArea.fillColor,
          weight: 4,
          opacity: 0.8,
        });
      } else {
        // --- POLYGON (existing behavior) ---
        const geometry = getGeometryFromLayer(layer);
        if (!geometry || !geometry.latlngs) return;

        paths = geometry.latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const measurements = enhancedGetPolygonArea(paths);
        const defaultType = AREA_TYPES.GR_CLEAN;

        newArea = {
          id: newId,
          name: '',
          areaType: defaultType,
          paths,
          fillColor: getAreaTypeColor(defaultType),
          strokeColor: '#000000',
          area: measurements.areaFeet.toFixed(2),
          areaAcres: measurements.areaAcres.toFixed(4),
          perimeter: measurements.perimeterFeet.toFixed(2),
          plantDensity: { ...DEFAULT_PLANT_DENSITY },
          modifiers: { ...DEFAULT_MODIFIERS },
          servicesRequired: getSuggestedServices(defaultType),
          notes: '',
          isNew: true,
        };

        layer.setStyle({
          fillColor: newArea.fillColor,
          fillOpacity: 0.3,
          color: '#000',
          weight: 2,
        });
      }

      // Store layer reference
      layerMapRef.current[newId] = layer;

      // Add click handler
      layer.on('click', () => {
        if (selectAreaRef.current) selectAreaRef.current(newId);
      });

      setAreas(prev => [...prev, newArea]);
      setSelectedAreaId(newId);
      setIsDrawing(false);
    });

    // (Per-polygon editing handled via handleStartEditShape / handleFinishEditShape)

    map.on('draw:drawstart', () => setIsDrawing(true));
    map.on('draw:drawstop', () => setIsDrawing(false));

    // Property lines feature group (separate from drawn items)
    const propertyLinesGroup = new L.FeatureGroup();
    map.addLayer(propertyLinesGroup);
    propertyLinesRef.current = propertyLinesGroup;

    mapInstanceRef.current = map;
    drawnItemsRef.current = drawnItems;
    drawControlRef.current = drawControl;

    // Load Maricopa County aerial providers
    loadProviders();

    return () => {
      map.remove();
      mapInstanceRef.current = null;
    };
  }, []);

  // =============================================
  // PLACEMENT QUEUE — map click handler
  // =============================================
  const placingItemIdRef = useRef(null);
  useEffect(() => { placingItemIdRef.current = placingItemId; }, [placingItemId]);

  const [placementPaused, setPlacementPaused] = useState(false);
  // Hold Space to pause placement (allows panning/zooming)
  useEffect(() => {
    if (!placingItemId) return;
    const onDown = (e) => { if (e.code === 'Space') { e.preventDefault(); setPlacementPaused(true); } };
    const onUp = (e) => { if (e.code === 'Space') { e.preventDefault(); setPlacementPaused(false); } };
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      setPlacementPaused(false);
    };
  }, [placingItemId]);

  // Placement click: use a transparent overlay div to capture clicks above ALL map layers
  const placementOverlayRef = useRef(null);

  const handlePlacementClick = useCallback((e) => {
    const map = mapInstanceRef.current;
    const currentPlacingId = placingItemIdRef.current;
    if (!map || !currentPlacingId) return;

    // Convert pixel click position to lat/lng
    const rect = mapContainerRef.current.getBoundingClientRect();
    const point = L.point(e.clientX - rect.left, e.clientY - rect.top);
    const ll = map.containerPointToLatLng(point);

    const item = placementQueue.find(q => q.queueId === currentPlacingId);
    if (!item) return;

    const typeDef = PLACEMENT_ITEM_TYPES[item.itemType];
    const areaType = typeDef?.defaultAreaType || AREA_TYPES.POINT_MARKER;
    const color = getAreaTypeColor(areaType);
    const newId = uuidv4();
    const paths = [{ lat: ll.lat, lng: ll.lng }];

    // Build treeDetails from queue item details (for tree types)
    let treeDetails;
    if (item.itemType === 'tree') {
      treeDetails = {
        species: item.details?.species || item.details?.commonName || '',
        height: item.details?.height || 0,
        caliper: item.details?.caliper || 0,
        canopySpread: item.details?.canopySpread || item.details?.spread || 0,
        condition: item.details?.condition || '',
      };
    }

    const newArea = {
      id: newId,
      geometryType: 'point',
      name: item.label || '',
      areaType,
      paths,
      area: '0',
      areaAcres: '0',
      perimeter: '0',
      fillColor: color,
      strokeColor: '#000000',
      treeDetails: treeDetails || undefined,
      plantDensity: { ...DEFAULT_PLANT_DENSITY, treeCount: item.itemType === 'tree' ? 1 : 0 },
      modifiers: { ...DEFAULT_MODIFIERS },
      servicesRequired: getSuggestedServices(areaType),
      notes: item.details?._raw
        ? Object.entries(item.details._raw).map(([k, v]) => v ? `${k}: ${v}` : '').filter(Boolean).join(' | ')
        : '',
      isNew: true,
      queueItemId: currentPlacingId,
    };

    // Add marker to map
    const marker = L.marker([ll.lat, ll.lng], {
      icon: L.divIcon({
        className: '',
        html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
        iconSize: [18, 18],
        iconAnchor: [9, 9],
      }),
    });
    drawnItemsRef.current.addLayer(marker);
    layerMapRef.current[newId] = marker;
    marker.on('click', () => {
      if (selectAreaRef.current) selectAreaRef.current(newId);
    });

    // Update state
    setAreas(prev => [...prev, newArea]);
    setSelectedAreaId(newId);

    // Mark queue item as placed
    setPlacementQueue(prev => prev.map(q =>
      q.queueId === currentPlacingId ? { ...q, status: 'placed', areaId: newId } : q
    ));

    // Auto-advance to next unplaced item AFTER the current one in the list
    const currentIdx = placementQueue.findIndex(q => q.queueId === currentPlacingId);
    const afterCurrent = placementQueue.slice(currentIdx + 1);
    const beforeCurrent = placementQueue.slice(0, currentIdx);
    // Look forward first, then wrap around
    const nextUnplaced = afterCurrent.find(q => q.status === 'unplaced') || beforeCurrent.find(q => q.status === 'unplaced');
    setPlacingItemId(nextUnplaced?.queueId || null);
  }, [placementQueue]);

  // =============================================
  // AERIAL PROVIDERS
  // =============================================
  const loadProviders = async () => {
    try {
      await loadEsriLeaflet();
      const currentYear = new Date().getFullYear();
      const yearsToCheck = [];
      for (let y = currentYear; y >= 2017; y--) yearsToCheck.push(y);

      const results = await Promise.all(yearsToCheck.map(y => checkYearService(y)));
      const valid = results.filter(Boolean);
      const allProviders = [googleHybridProvider, googleSatelliteProvider, googleMapsProvider, ...valid];
      setProviders(allProviders);
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  // =============================================
  // PROPERTY LINES
  // =============================================
  const fetchPropertyLines = useCallback(async (siteId) => {
    try {
      const response = await axios.get(`${url}/api/customer/getAPNAndParcelInfo?id=${siteId}&includeManual=true`);
      const fetched = [];
      console.log('fetchPropertyLines..', response)
      if (response?.data?.propertyLines?.length) {
        response.data.propertyLines
          .filter(property => property?.googleMapsFormat?.length > 0)
          .forEach((property, index) => {
            const parcelData = property.properties || {};
            const paths = property.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng }));
            fetched.push({
              id: `prop_${siteId}_${parcelData.APN || index}`,
              APN: parcelData.APN,
              address: parcelData.address,
              paths,
              acres: parcelData.acres,
              sf: parcelData.sf,
              aspirePropertyId: parcelData.aspirePropertyId
            });
          });
      }

      // Include manual property lines if present
      if (response?.data?.manualPropertyLines?.length) {
        response.data.manualPropertyLines.forEach((line, index) => {
          if (!line.paths?.length) return;
          fetched.push({
            id: line._id || `manual_${siteId}_${index}`,
            APN: line.APN || '',
            name: line.name || 'Manual',
            paths: line.paths,
            acres: parcelData.acres,
            sf: parcelData.sf,
            aspirePropertyId: parcelData.aspirePropertyId
          });
        });
      }
      if (response?.data?.length && response?.data[0]?.APN) {
        console.log('use this fucker...', response.data[0])
        const parcelData = response.data[0] || {};
        const paths = parcelData.googleMapsFormat.map(p => ({ lat: p.lat, lng: p.lng }));
        fetched.push({
          id: `prop_${siteId}_${parcelData.APN || index}`,
          APN: parcelData.APN,
          address: parcelData.address,
          paths,
          acres: parcelData.acres,
          sf: parcelData.sf,
          aspirePropertyId: parcelData.aspirePropertyId
        });
      }
      setPropertyLines(fetched);
      if (fetched.length > 0) {
        showSnackbar(`Loaded ${fetched.length} property boundaries`, 'success');
      }
    } catch (err) {
      console.error('Error fetching property lines:', err);
    }
  }, [url]);

  // Render property lines on the map whenever they change or visibility toggles
  useEffect(() => {
    if (!propertyLinesRef.current) return;
    propertyLinesRef.current.clearLayers();

    if (!showPropertyLines || !propertyLines.length) return;

    propertyLines.forEach((line) => {
      if (!line.paths?.length) return;
      const layer = L.polygon(line.paths, {
        color: '#4caf50',
        weight: 3,
        opacity: 0.8,
        fillColor: '#4caf50',
        fillOpacity: 0.1,
        interactive: false,
      });
      if (line.APN || line.name) {
        layer.bindTooltip(`${line.APN || ''} ${line.name || ''}`.trim());
      }
      propertyLinesRef.current.addLayer(layer);
    });
  }, [propertyLines, showPropertyLines]);

  const handleProviderChange = useCallback((provider) => {
    if (!mapInstanceRef.current) return;
    setCurrentProvider(provider);

    if (tileLayerRef.current) {
      mapInstanceRef.current.removeLayer(tileLayerRef.current);
    }

    if (provider.isExportService && window.L?.esri?.dynamicMapLayer) {
      // ESRI dynamic layers don't support maxNativeZoom — convert to tile layer if possible
      // For export services, keep them as dynamic but limit to their native zoom
      tileLayerRef.current = window.L.esri.dynamicMapLayer({
        url: provider.url,
        maxZoom: 28,
      }).addTo(mapInstanceRef.current);
    } else {
      tileLayerRef.current = L.tileLayer(provider.url, {
        attribution: provider.attribution,
        maxNativeZoom: provider.maxZoom || 20,
        maxZoom: 28,
        errorTileUrl: '',
      }).addTo(mapInstanceRef.current);
    }
  }, []);

  // =============================================
  // GIS OVERLAY LAYERS
  // =============================================
  const GIS_OVERLAYS = [
    { key: 'contours', label: 'Topographic (10ft Contours)', color: '#FF6D00', type: 'dynamic',
      url: 'https://gis.maricopa.gov/arcgis/rest/services/GIO/Countywide10FootContour/MapServer', opacity: 1.0 },
    { key: 'flood', label: 'FEMA Flood Zones', color: '#1565C0', type: 'dynamic',
      url: 'https://gis.maricopa.gov/arcgis/rest/services/CompositeService/FloodDataFEMA/MapServer', opacity: 0.5 },
    { key: 'parcels', label: 'Parcel Outlines', color: '#FF3D00', type: 'feature',
      url: 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/Parcels/MapServer/0', opacity: 1.0,
      style: { color: '#FF3D00', weight: 3, fillOpacity: 0, opacity: 1 } },
    { key: 'parcelLabels', label: 'Parcel Labels (APN)', color: '#4E342E', type: 'dynamic',
      url: 'https://gis.mcassessor.maricopa.gov/arcgis/rest/services/ParcelLabel/MapServer', opacity: 0.8 },
    { key: 'streets', label: 'Streets', color: '#616161', type: 'dynamic',
      url: 'https://gis.maricopa.gov/arcgis/rest/services/IndividualService/Street/MapServer', opacity: 0.6 },
    { key: 'streetTerrain', label: 'Street + Terrain Basemap', color: '#558B2F', type: 'tile',
      url: 'https://gis.maricopa.gov/arcgis/rest/services/Basemap/StreetWithTerrain/MapServer/tile/{z}/{y}/{x}', opacity: 0.5, maxZoom: 13 },
  ];

  const handleToggleGisOverlay = useCallback((overlayDef) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const existing = gisOverlayLayersRef.current[overlayDef.key];
    if (existing) {
      // Remove
      map.removeLayer(existing);
      delete gisOverlayLayersRef.current[overlayDef.key];
      setActiveGisOverlays(prev => prev.filter(k => k !== overlayDef.key));
    } else {
      // Add
      let layer;
      if (overlayDef.type === 'feature' && window.L?.esri?.featureLayer) {
        layer = window.L.esri.featureLayer({
          url: overlayDef.url,
          style: () => overlayDef.style || { color: overlayDef.color, weight: 2, fillOpacity: 0 },
        }).addTo(map);
      } else if (overlayDef.type === 'dynamic' && window.L?.esri?.dynamicMapLayer) {
        layer = window.L.esri.dynamicMapLayer({
          url: overlayDef.url,
          opacity: overlayDef.opacity || 0.6,
        }).addTo(map);
      } else if (overlayDef.type === 'tile') {
        layer = L.tileLayer(overlayDef.url, {
          opacity: overlayDef.opacity || 0.5,
          maxNativeZoom: overlayDef.maxZoom || 20,
          maxZoom: 28,
          errorTileUrl: '',
          attribution: 'Maricopa County GIS',
        }).addTo(map);
      } else {
        layer = L.tileLayer(overlayDef.url, {
          opacity: overlayDef.opacity || 0.5,
          maxNativeZoom: overlayDef.maxZoom || 20,
          maxZoom: 28,
          errorTileUrl: '',
          attribution: 'Maricopa County GIS',
        }).addTo(map);
      }
      gisOverlayLayersRef.current[overlayDef.key] = layer;
      setActiveGisOverlays(prev => [...prev, overlayDef.key]);
    }
  }, []);

  // =============================================
  // TAKEOFF HELPERS
  // =============================================
  const clearMapAreas = useCallback(() => {
    if (drawnItemsRef.current) drawnItemsRef.current.clearLayers();
    layerMapRef.current = {};
    setAreas([]);
    setSelectedAreaId(null);
    // Remove all overlays
    const map = mapInstanceRef.current;
    Object.values(overlayLayersRef.current).forEach(layer => {
      try {
        if (layer.editing) layer.editing.disable();
        layer.remove();
      } catch (e) {
        try { if (map) map.removeLayer(layer); } catch (_) {}
      }
    });
    overlayLayersRef.current = {};
    setOverlays([]);
  }, []);

  const renderAreasOnMap = useCallback((loadedAreas) => {
    loadedAreas.forEach(area => {
      if (!area.paths?.length || !drawnItemsRef.current) return;
      const color = getAreaTypeColor(area.areaType);
      let mapLayer;

      if (area.geometryType === 'point') {
        const p = area.paths[0];
        mapLayer = L.marker([p.lat, p.lng], {
          icon: L.divIcon({
            className: '',
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
            iconSize: [18, 18],
            iconAnchor: [9, 9],
          }),
        });
      } else if (area.geometryType === 'ellipse') {
        const p = area.paths[0];
        // Convert feet back to meters for Leaflet circle
        const radiusMeters = (area.radiusX || 0) / 3.28084;
        mapLayer = L.circle([p.lat, p.lng], {
          radius: radiusMeters,
          fillColor: color, fillOpacity: 0.25,
          color, weight: 2,
        });
      } else if (area.geometryType === 'polyline') {
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        mapLayer = L.polyline(latlngs, { color, weight: 4, opacity: 0.8 });
      } else {
        const latlngs = area.paths.map(p => [p.lat, p.lng]);
        mapLayer = L.polygon(latlngs, {
          fillColor: color, fillOpacity: 0.3,
          color: area.strokeColor || '#000', weight: 2,
        });
      }

      drawnItemsRef.current.addLayer(mapLayer);
      layerMapRef.current[area._id || area.id] = mapLayer;
      mapLayer.on('click', () => {
        if (selectAreaRef.current) selectAreaRef.current(area._id || area.id);
      });
    });
  }, []);

  const restoreOverlays = useCallback((overlaysArray) => {
    const map = mapInstanceRef.current;
    if (!map || !Array.isArray(overlaysArray) || !overlaysArray.length) return;

    // Defer until the marker pane exists (leaflet-distortableimage needs it for its icon SVGs)
    const doRestore = () => {
      const restored = [];
      overlaysArray.forEach((ov) => {
        if (!ov?.imageUrl || !ov?.corners?.length || ov.corners.length !== 4) return;
        const id = uuidv4();
        const corners = ov.corners.map(c => L.latLng(c.lat, c.lng));
        const opacity = ov.opacity ?? 0.6;
        const isLocked = !!ov.locked;
        const layer = L.distortableImageOverlay(ov.imageUrl, {
          corners,
          mode: isLocked ? 'lock' : 'rotate',
          selected: false,
          opacity,
        }).addTo(map);

        overlayLayersRef.current[id] = layer;
        restored.push({ id, imageUrl: ov.imageUrl, visible: true, opacity, locked: isLocked, category: ov.category || '', subType: ov.subType || '' });
      });
      setOverlays(restored);
    };

    if (map.getPane('markerPane')) {
      doRestore();
    } else {
      // Wait for map to be ready
      map.whenReady(doRestore);
    }
  }, []);

  const loadTakeoffById = useCallback(async (takeoffId) => {
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoff/${takeoffId}`);
      const { success, takeoff } = resp.data;
      if (!success || !takeoff) return;

      clearMapAreas();
      setActiveTakeoffId(takeoff._id);
      setTakeoffName(takeoff.name || '');

      // Restore rate overrides
      if (takeoff.productionRates && typeof takeoff.productionRates === 'object') {
        setRateOverrides(takeoff.productionRates);
      } else {
        setRateOverrides({});
      }

      // Load areas
      const areasToLoad = (takeoff.areas || [])
        .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0] && 'lng' in a.paths[0])
        .map(a => ({
          ...a,
          id: a.id || a._id,
          plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
          modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
          servicesRequired: a.servicesRequired?.length ? a.servicesRequired : getSuggestedServices(a.areaType),
          notes: a.notes || '',
          isNew: false,
        }));

      setAreas(areasToLoad);
      renderAreasOnMap(areasToLoad);

      // Restore overlays
      if (takeoff.overlays?.length) {
        restoreOverlays(takeoff.overlays);
      }

      // Restore placement queue
      setPlacementQueue(takeoff.placementQueue || []);
      setPlacingItemId(null);

      showSnackbar(`Loaded takeoff "${takeoff.name}" with ${areasToLoad.length} areas`, 'success');
    } catch (err) {
      console.error('Error loading takeoff:', err);
      showSnackbar('Error loading takeoff', 'error');
    }
  }, [url, clearMapAreas, renderAreasOnMap, restoreOverlays]);

  const fetchTakeoffList = useCallback(async (jobSiteId) => {
    try {
      const resp = await axios.get(`${url}/api/customer/site-takeoffs?jobSiteId=${jobSiteId}`);
      if (resp.data?.success) {
        setTakeoffs(resp.data.takeoffs || []);
        setSavedOverlayUrls(resp.data.overlayUrls || []);
        return resp.data.takeoffs || [];
      }
    } catch (err) {
      console.error('Error fetching takeoffs:', err);
    }
    return [];
  }, [url]);

  // =============================================
  // SITE LOADING
  // =============================================
  useEffect(() => {
    if (jobsiteId) {
      // Clear previous site data
      clearMapAreas();
      if (propertyLinesRef.current) propertyLinesRef.current.clearLayers();
      setPropertyLines([]);
      setTakeoffs([]);
      setActiveTakeoffId(null);
      setTakeoffName('');
      setHasLegacyAreas(false);
      setSavedOverlayUrls([]);
      setPlacementQueue([]);
      setPlacingItemId(null);
      loadJobSite(jobsiteId);
    }
  }, [jobsiteId]);

  const loadJobSite = async (siteId) => {
    console.log('loadJobSite', siteId)
    try {
      const resp = await axios.get(`${url}/api/customer/fetchJobSiteAreas?id=${siteId}&includeAll=1`);
      const { success, jobSite: siteData, jobSiteAreas } = resp.data;
      if (!success || !siteData?._id) return;
      console.log('loadJobSite data', siteData)
      setJobSite(siteData);
      setSelectedSite(siteData);

      if (siteData.client) {
        const client = typeof siteData.client === 'object' ? siteData.client : { _id: siteData.client };
        setSelectedClient(client);
        if (client._id) fetchClientSites(client._id);
      }

      // Center map on site
      if (mapInstanceRef.current) {
        let centerCoords = null;
        if (siteData.location?.coordinates?.length === 2) {
          centerCoords = [siteData.location.coordinates[1], siteData.location.coordinates[0]];
        } else if (siteData.latitude && siteData.longitude) {
          centerCoords = [siteData.latitude, siteData.longitude];
        }

        if (centerCoords) {
          const zoomLevel = (jobSiteAreas?.length > 0) ? 18 : 17;
          mapInstanceRef.current.setView(centerCoords, zoomLevel);
        }
      }

      // Fetch property lines for this site
      fetchPropertyLines(siteId);

      // Fetch takeoffs for this site
      const takeoffList = await fetchTakeoffList(siteId);

      if (takeoffList.length > 0) {
        // Auto-load the most recent takeoff
        setHasLegacyAreas(false);
        await loadTakeoffById(takeoffList[0]._id);
      } else {
        // No takeoffs — check if there are legacy JobSite.areas
        const legacyAreas = jobSiteAreas?.length ? jobSiteAreas : [];
        setHasLegacyAreas(legacyAreas.length > 0);
        setRateOverrides({});

        if (legacyAreas.length) {
          // Show legacy areas on map (read-only until imported)
          const loaded = legacyAreas
            .filter(a => Array.isArray(a.paths) && a.paths.length > 0 && 'lat' in a.paths[0] && 'lng' in a.paths[0])
            .map(a => ({
              ...a,
              id: a.id || a._id,
              plantDensity: a.plantDensity || { ...DEFAULT_PLANT_DENSITY },
              modifiers: a.modifiers || { ...DEFAULT_MODIFIERS },
              servicesRequired: a.servicesRequired?.length ? a.servicesRequired : getSuggestedServices(a.areaType),
              notes: a.notes || '',
              isNew: false,
            }));
          setAreas(loaded);
          renderAreasOnMap(loaded);
          showSnackbar(`Found ${loaded.length} legacy areas — create a takeoff or import them`, 'info');
        }
      }
    } catch (err) {
      console.error('Error loading jobsite:', err);
      showSnackbar('Error loading jobsite', 'error');
    }
  };

  // =============================================
  // CLIENT / SITE SELECTION
  // =============================================
  const fetchClientSites = async (clientId) => {
    try {
      const resp = await axios.get(`${url}/api/customer/getAllJobsites?id=${clientId}&sitesOnly=true`);
      setAvailableSites(resp.data || []);
    } catch (err) {
      console.error('Error fetching sites:', err);
    }
  };

  const handleClientChange = useCallback((e, newClient) => {
    setSelectedClient(newClient || {});
    setSelectedSite({});
    setAvailableSites([]);
    if (newClient?._id) fetchClientSites(newClient._id);
  }, [url]);

  const handleSiteChange = useCallback((e, newSite) => {
    if (!newSite?._id) return;
    clearMapAreas();
    setSelectedSite(newSite);
    setTakeoffs([]);
    setActiveTakeoffId(null);
    setTakeoffName('');
    setHasLegacyAreas(false);
    // Navigate to the site — useEffect on jobsiteId will trigger loadJobSite
    history.push(`/site-takeoff/${newSite._id}`);
  }, [history, clearMapAreas]);

  // =============================================
  // AREA EDITING
  // =============================================
  const handleUpdateArea = useCallback((areaId, field, value) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;

      const updated = { ...a };

      // Handle nested fields
      if (field === 'treeDetails') {
        updated.treeDetails = { ...(a.treeDetails || DEFAULT_TREE_DETAILS), ...value };
      } else if (field === 'plantDensity') {
        updated.plantDensity = { ...(a.plantDensity || DEFAULT_PLANT_DENSITY), ...value };
      } else if (field === 'modifiers') {
        updated.modifiers = { ...(a.modifiers || DEFAULT_MODIFIERS), ...value };
      } else if (field === 'servicesRequired') {
        updated.servicesRequired = value;
      } else if (field === 'areaType') {
        updated.areaType = value;
        // Update color on map
        const color = getAreaTypeColor(value);
        updated.fillColor = color;
        const layer = layerMapRef.current[areaId];
        if (layer) {
          if (a.geometryType === 'point') {
            layer.setIcon(L.divIcon({
              className: '',
              html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
              iconSize: [18, 18],
              iconAnchor: [9, 9],
            }));
          } else if (a.geometryType === 'ellipse') {
            layer.setStyle({ fillColor: color, color, fillOpacity: 0.25 });
          } else if (a.geometryType === 'polyline') {
            layer.setStyle({ color, weight: 4, opacity: 0.8 });
          } else {
            layer.setStyle({ fillColor: color, fillOpacity: 0.3 });
          }
        }
        // Auto-suggest services for new type (only if user hasn't manually edited)
        updated.servicesRequired = getSuggestedServices(value);
      } else {
        updated[field] = value;
      }

      return updated;
    }));
  }, []);

  // Helper: disable editing on a layer and restore its style
  const finishEditOnLayer = useCallback((id) => {
    const layer = layerMapRef.current[id];
    if (!layer || !layer.editing) return;
    layer.editing.disable();

    const area = areasRef.current.find(a => (a._id || a.id) === id);
    const isPolyline = area?.geometryType === 'polyline';

    // Recalculate geometry
    if (isPolyline) {
      const latlngs = layer.getLatLngs();
      const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
      const lengthFeet = calculateLineLengthFeet(paths);
      const widthFt = area?.width || DEFAULT_LINE_WIDTH;
      const areaSF = lengthFeet * widthFt;

      setAreas(prev => prev.map(a => {
        if ((a._id || a.id) !== id) return a;
        return {
          ...a,
          paths,
          length: lengthFeet.toFixed(2),
          area: areaSF.toFixed(2),
          areaAcres: (areaSF / 43560).toFixed(4),
        };
      }));

      // Restore polyline style (stroke only)
      layer.setStyle({
        weight: 4, dashArray: null, opacity: 0.8,
        color: area ? area.fillColor : '#1976d2',
      });
    } else {
      const geometry = getGeometryFromLayer(layer);
      if (geometry && geometry.latlngs) {
        const paths = geometry.latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
        const measurements = enhancedGetPolygonArea(paths);

        setAreas(prev => prev.map(a => {
          if ((a._id || a.id) !== id) return a;
          return {
            ...a,
            paths,
            area: measurements.areaFeet.toFixed(2),
            areaAcres: measurements.areaAcres.toFixed(4),
            perimeter: measurements.perimeterFeet.toFixed(2),
          };
        }));
      }

      // Restore polygon style
      layer.setStyle({
        weight: 2, dashArray: null, color: '#000',
        fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3,
      });
    }
  }, []);

  // Finish editing and recalculate measurements
  const handleFinishEditShape = useCallback((areaId) => {
    const id = areaId || editingAreaId;
    if (!id) return;
    finishEditOnLayer(id);
    setEditingAreaId(null);
  }, [editingAreaId, finishEditOnLayer]);

  // Start editing a single polygon's shape
  const handleStartEditShape = useCallback((areaId) => {
    // Finish any existing edit first
    if (editingAreaId) {
      finishEditOnLayer(editingAreaId);
    }
    const layer = layerMapRef.current[areaId];
    if (layer && layer.editing) {
      layer.editing.enable();
      layer.setStyle({ weight: 3, dashArray: '5, 8', color: '#1976d2' });
      setEditingAreaId(areaId);
      setSelectedAreaId(areaId);
    }
  }, [editingAreaId, finishEditOnLayer]);

  // Cancel edit without saving changes — revert polygon to stored paths
  const handleCancelEditShape = useCallback(() => {
    if (!editingAreaId) return;
    const layer = layerMapRef.current[editingAreaId];
    const area = areasRef.current.find(a => (a._id || a.id) === editingAreaId);
    if (layer && layer.editing) {
      layer.editing.disable();
      // Revert to saved paths
      if (area && area.paths && area.paths.length > 0) {
        const latlngs = area.paths.map(p => L.latLng(p.lat, p.lng));
        layer.setLatLngs(latlngs);
      }
      if (area?.geometryType === 'polyline') {
        layer.setStyle({
          weight: 4, dashArray: null, opacity: 0.8,
          color: area ? area.fillColor : '#1976d2',
        });
      } else {
        layer.setStyle({
          weight: 2, dashArray: null, color: '#000',
          fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3,
        });
      }
    }
    setEditingAreaId(null);
  }, [editingAreaId]);

  const handleSelectArea = useCallback((areaId) => {
    // Finish any in-progress shape edit before switching
    if (editingAreaId && editingAreaId !== areaId) {
      finishEditOnLayer(editingAreaId);
      setEditingAreaId(null);
    }

    // Un-highlight previously selected — restore original style
    if (selectedAreaId && selectedAreaId !== areaId) {
      const prevLayer = layerMapRef.current[selectedAreaId];
      if (prevLayer && (!editingAreaId || editingAreaId !== selectedAreaId)) {
        const prevArea = areasRef.current.find(a => (a._id || a.id) === selectedAreaId);
        if (prevArea?.geometryType === 'point' || prevLayer.setIcon) {
          const c = prevArea?.fillColor || '#228B22';
          prevLayer.setIcon(L.divIcon({
            className: '',
            html: `<div style="width:18px;height:18px;border-radius:50%;background:${c};border:2px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,0.4);"></div>`,
            iconSize: [18, 18], iconAnchor: [9, 9],
          }));
        } else if (prevArea?.geometryType === 'ellipse') {
          prevLayer.setStyle({
            weight: 2, color: prevArea.fillColor, opacity: 0.8,
            fillColor: prevArea.fillColor, fillOpacity: 0.25,
          });
        } else if (prevArea?.geometryType === 'polyline') {
          prevLayer.setStyle({
            weight: 4, color: prevArea?.fillColor || '#1976d2', opacity: 0.8,
          });
        } else if (prevLayer.setStyle) {
          prevLayer.setStyle({
            weight: 2, color: '#000', opacity: 0.8,
            fillColor: prevArea ? prevArea.fillColor : '#3388ff',
            fillOpacity: 0.3,
          });
        }
      }
    }

    // Highlight newly selected
    const layer = layerMapRef.current[areaId];
    if (layer) {
      const selArea = areasRef.current.find(a => (a._id || a.id) === areaId);
      if (selArea?.geometryType === 'point' || layer.setIcon) {
        layer.setIcon(L.divIcon({
          className: '',
          html: `<div style="width:24px;height:24px;border-radius:50%;background:#ffeb3b;border:3px solid #ff9800;box-shadow:0 2px 6px rgba(0,0,0,0.5);"></div>`,
          iconSize: [24, 24], iconAnchor: [12, 12],
        }));
      } else if (selArea?.geometryType === 'polyline') {
        layer.setStyle({ weight: 6, color: '#ffeb3b', opacity: 1 });
      } else if (layer.setStyle) {
        layer.setStyle({
          weight: 4, color: '#ffeb3b', opacity: 1,
          fillColor: '#ffeb3b', fillOpacity: 0.45,
        });
      }
      if (layer.bringToFront) layer.bringToFront();
      if (mapInstanceRef.current) {
        if (layer.getBounds) {
          mapInstanceRef.current.fitBounds(layer.getBounds(), { padding: [50, 50], maxZoom: 19 });
        } else if (layer.getLatLng) {
          mapInstanceRef.current.setView(layer.getLatLng(), Math.max(mapInstanceRef.current.getZoom(), 18));
        }
      }
    }

    setSelectedAreaId(areaId);
    setEditorTab(0);
  }, [editingAreaId, finishEditOnLayer, selectedAreaId]);

  // Keep ref in sync so map click handlers can use it
  useEffect(() => { selectAreaRef.current = handleSelectArea; }, [handleSelectArea]);

  const handleDeleteArea = useCallback((areaId) => {
    // Cancel edit if deleting the area being edited
    if (editingAreaId === areaId) setEditingAreaId(null);
    const layer = layerMapRef.current[areaId];
    if (layer) {
      if (layer.editing) layer.editing.disable();
      if (drawnItemsRef.current) drawnItemsRef.current.removeLayer(layer);
    }
    delete layerMapRef.current[areaId];
    setAreas(prev => prev.filter(a => (a._id || a.id) !== areaId));
    if (selectedAreaId === areaId) setSelectedAreaId(null);
  }, [selectedAreaId, editingAreaId]);

  // =============================================
  // MERGE AREAS
  // =============================================
  const handleToggleMergeSelect = useCallback((areaId) => {
    // Don't allow polylines in merge — unionPolygons can't handle open lines
    const area = areasRef.current.find(a => (a._id || a.id) === areaId);
    if (area?.geometryType === 'polyline') {
      showSnackbar('Lines cannot be merged — only polygons', 'info');
      return;
    }
    setMergeSelection(prev => {
      const isRemoving = prev.includes(areaId);
      const layer = layerMapRef.current[areaId];
      if (layer) {
        if (isRemoving) {
          // Restore normal style
          const area = areasRef.current.find(a => (a._id || a.id) === areaId);
          layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8 });
        } else {
          // Highlight for merge
          layer.setStyle({ weight: 4, color: '#ff9800', dashArray: '6, 4', opacity: 1 });
          layer.bringToFront();
        }
      }
      if (isRemoving) return prev.filter(id => id !== areaId);
      return [...prev, areaId];
    });
  }, []);

  const handleMergeAreas = useCallback(() => {
    if (mergeSelection.length < 2) {
      showSnackbar('Select at least 2 areas to merge', 'error');
      return;
    }

    const selectedAreas = mergeSelection
      .map(id => areasRef.current.find(a => (a._id || a.id) === id))
      .filter(Boolean);

    if (selectedAreas.length < 2) return;

    try {
      // Union all selected polygons
      const polyPaths = selectedAreas.map(a => a.paths);
      const paths = unionPolygons(polyPaths);

      if (!paths || paths.length < 3) {
        showSnackbar('Could not merge — try selecting adjacent areas', 'error');
        return;
      }

      const measurements = enhancedGetPolygonArea(paths);
      const primary = selectedAreas[0]; // first selected area is the "primary"
      const newId = uuidv4();

      const mergedArea = {
        id: newId,
        name: primary.name || getAreaTypeLabel(primary.areaType),
        areaType: primary.areaType,
        paths,
        fillColor: primary.fillColor,
        strokeColor: primary.strokeColor || '#000000',
        area: measurements.areaFeet.toFixed(2),
        areaAcres: measurements.areaAcres.toFixed(4),
        perimeter: measurements.perimeterFeet.toFixed(2),
        plantDensity: { ...DEFAULT_PLANT_DENSITY, ...(primary.plantDensity || {}) },
        modifiers: { ...DEFAULT_MODIFIERS, ...(primary.modifiers || {}) },
        servicesRequired: [...(primary.servicesRequired || [])],
        notes: selectedAreas.map(a => a.notes).filter(Boolean).join('; '),
        isNew: true,
      };

      // Sum tree counts from all merged areas
      mergedArea.plantDensity.treeCount = selectedAreas.reduce(
        (sum, a) => sum + (a.plantDensity?.treeCount || 0), 0
      );

      // Remove old layers from map
      mergeSelection.forEach(id => {
        const layer = layerMapRef.current[id];
        if (layer) {
          if (layer.editing) layer.editing.disable();
          if (drawnItemsRef.current) drawnItemsRef.current.removeLayer(layer);
        }
        delete layerMapRef.current[id];
      });

      // Add new merged polygon to map
      const latlngs = paths.map(p => L.latLng(p.lat, p.lng));
      const newLayer = L.polygon(latlngs, {
        fillColor: mergedArea.fillColor,
        fillOpacity: 0.3,
        color: '#000',
        weight: 2,
      });
      drawnItemsRef.current.addLayer(newLayer);
      layerMapRef.current[newId] = newLayer;
      newLayer.on('click', () => {
        if (selectAreaRef.current) selectAreaRef.current(newId);
      });

      // Update state: remove old areas, add merged
      setAreas(prev => {
        const filtered = prev.filter(a => !mergeSelection.includes(a._id || a.id));
        return [...filtered, mergedArea];
      });

      setSelectedAreaId(newId);
      setMergeSelection([]);
      setMergeMode(false);
      showSnackbar(`Merged ${selectedAreas.length} areas into "${mergedArea.name}"`, 'success');
    } catch (err) {
      console.error('Merge error:', err);
      showSnackbar('Merge failed: ' + (err.message || 'Unknown error'), 'error');
    }
  }, [mergeSelection]);

  // =============================================
  // GROUPING (logical — no geometry merge)
  // =============================================
  const handleToggleGroupSelect = useCallback((areaId) => {
    setGroupSelection(prev => {
      const isRemoving = prev.includes(areaId);
      const layer = layerMapRef.current[areaId];
      if (layer) {
        if (isRemoving) {
          const area = areasRef.current.find(a => (a._id || a.id) === areaId);
          layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8,
            fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3 });
        } else {
          layer.setStyle({ weight: 4, color: '#7b1fa2', dashArray: '6, 4', opacity: 1,
            fillColor: '#ce93d8', fillOpacity: 0.4 });
          layer.bringToFront();
        }
      }
      if (isRemoving) return prev.filter(id => id !== areaId);
      return [...prev, areaId];
    });
  }, []);

  const handleCreateGroup = useCallback(() => {
    if (groupSelection.length < 2) {
      showSnackbar('Select at least 2 areas to group', 'error');
      return;
    }

    // Build a group name from the area types
    const selectedAreas = groupSelection
      .map(id => areasRef.current.find(a => (a._id || a.id) === id))
      .filter(Boolean);

    const primaryType = selectedAreas[0]?.areaType;
    const defaultName = getAreaTypeLabel(primaryType) + ' Group';

    const name = window.prompt('Group name:', defaultName);
    if (!name) return; // cancelled

    const gid = uuidv4();

    // Restore polygon styles before updating state
    groupSelection.forEach(id => {
      const layer = layerMapRef.current[id];
      const area = areasRef.current.find(a => (a._id || a.id) === id);
      if (layer) {
        layer.setStyle({ weight: 2, color: '#000', dashArray: null, opacity: 0.8,
          fillColor: area ? area.fillColor : '#3388ff', fillOpacity: 0.3 });
      }
    });

    setAreas(prev => prev.map(a => {
      if (!groupSelection.includes(a._id || a.id)) return a;
      return { ...a, groupId: gid, groupName: name };
    }));

    setGroupSelection([]);
    setGroupMode(false);
    showSnackbar(`Grouped ${selectedAreas.length} areas as "${name}"`, 'success');
  }, [groupSelection]);

  const handleUngroupArea = useCallback((areaId) => {
    setAreas(prev => prev.map(a => {
      if ((a._id || a.id) !== areaId) return a;
      const { groupId, groupName, ...rest } = a;
      return rest;
    }));
  }, []);

  const handleUngroupAll = useCallback((groupId) => {
    setAreas(prev => prev.map(a => {
      if (a.groupId !== groupId) return a;
      const { groupId: _gid, groupName: _gn, ...rest } = a;
      return rest;
    }));
    showSnackbar('Group removed', 'success');
  }, []);

  // =============================================
  // SAVE
  // =============================================
  const handleSave = async () => {
    if (!jobSite?._id) {
      showSnackbar('No jobsite selected', 'error');
      return;
    }

    // Prompt for takeoff name if new
    let name = takeoffName;
    if (!name) {
      name = window.prompt('Name this takeoff:', 'Site Takeoff');
      if (!name) return;
      setTakeoffName(name);
    }

    setSaving(true);
    try {
      const areasToSave = areas.map(a => ({
        _id: a._id || undefined,
        name: a.name || getAreaTypeLabel(a.areaType),
        areaType: a.areaType,
        geometryType: a.geometryType || 'polygon',
        paths: a.paths,
        fillColor: a.fillColor,
        strokeColor: a.strokeColor || '#000000',
        area: a.area,
        areaAcres: a.areaAcres,
        perimeter: a.perimeter,
        length: a.length || '0',
        width: a.width || 0,
        radiusX: a.radiusX || 0,
        radiusY: a.radiusY || 0,
        treeDetails: a.treeDetails || undefined,
        plantDensity: a.plantDensity || DEFAULT_PLANT_DENSITY,
        modifiers: a.modifiers || DEFAULT_MODIFIERS,
        servicesRequired: a.servicesRequired || [],
        notes: a.notes || '',
        groupId: a.groupId || undefined,
        groupName: a.groupName || undefined,
      }));

      // Collect overlay data from all live overlays
      const overlaysData = overlays.map(ov => {
        const layer = overlayLayersRef.current[ov.id];
        if (!layer) return null;
        try {
          const corners = layer.getCorners();
          const imgUrl = layer._url || layer.getElement()?.src;
          if (corners?.length === 4 && imgUrl) {
            const isLocked = layer.editing?._mode === 'lock';
            return {
              imageUrl: imgUrl,
              corners: corners.map(c => ({ lat: c.lat, lng: c.lng })),
              opacity: ov.opacity ?? 0.6,
              locked: isLocked,
              category: ov.category || '',
              subType: ov.subType || '',
            };
          }
        } catch (e) {
          console.warn('Could not read overlay data:', e);
        }
        return null;
      }).filter(Boolean);

      const resp = await axios.post(`${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`, {
        takeoffId: activeTakeoffId || undefined,
        takeoffName: name,
        jobSite,
        areas: areasToSave,
        overlays: overlaysData,
        placementQueue: placementQueue,
        productionRates: Object.keys(rateOverrides).length > 0 ? rateOverrides : undefined,
      });

      if (resp.data?.success && resp.data?.takeoff?._id) {
        setActiveTakeoffId(resp.data.takeoff._id);
        setHasLegacyAreas(false);
        // Refresh takeoff list (sidebar dropdown)
        fetchTakeoffList(jobSite._id);
        // Patch area _ids from server response so next save knows which to update
        const serverAreaIds = resp.data.takeoff.areaIds || [];
        if (serverAreaIds.length === areasToSave.length) {
          setAreas(prev => prev.map((a, i) => ({ ...a, _id: serverAreaIds[i], isNew: false })));
        } else {
          // Fallback: mark as saved
          setAreas(prev => prev.map(a => ({ ...a, isNew: false })));
        }
        showSnackbar('Takeoff saved successfully', 'success');
      } else {
        showSnackbar(resp.data?.message || 'Failed to save takeoff', 'error');
      }
    } catch (err) {
      console.error('Error saving takeoff:', err);
      showSnackbar('Error saving takeoff', 'error');
    } finally {
      setSaving(false);
    }
  };

  // =============================================
  // TAKEOFF MANAGEMENT
  // =============================================
  const handleNewTakeoff = () => {
    const name = window.prompt('New takeoff name:', '');
    if (!name) return;
    clearMapAreas();
    setActiveTakeoffId(null);
    setTakeoffName(name);
    setRateOverrides({});
    setPlacementQueue([]);
    setPlacingItemId(null);
    showSnackbar(`New takeoff "${name}" — draw areas and save`, 'info');
  };

  const handleDeleteTakeoff = async () => {
    if (!activeTakeoffId) return;
    if (!window.confirm(`Delete takeoff "${takeoffName}"? This will permanently remove all its areas.`)) return;

    try {
      await axios.delete(`${url}/api/customer/site-takeoff/${activeTakeoffId}`);
      clearMapAreas();
      setActiveTakeoffId(null);
      setTakeoffName('');
      setRateOverrides({});

      // Refresh list and load next available
      const list = await fetchTakeoffList(jobSite._id);
      if (list.length > 0) {
        await loadTakeoffById(list[0]._id);
      }

      showSnackbar('Takeoff deleted', 'success');
    } catch (err) {
      console.error('Error deleting takeoff:', err);
      showSnackbar('Error deleting takeoff', 'error');
    }
  };

  const handleSwitchTakeoff = async (takeoffId) => {
    if (takeoffId === activeTakeoffId) return;
    await loadTakeoffById(takeoffId);
  };

  const handleImportLegacyAreas = async () => {
    if (!jobSite?._id || !areas.length) return;
    const name = window.prompt('Name for imported takeoff:', 'Imported Areas');
    if (!name) return;

    setSaving(true);
    try {
      // Strip _id to create copies — never update the original JobSite.areas
      const areasToSave = areas.map(a => ({
        name: a.name || getAreaTypeLabel(a.areaType),
        areaType: a.areaType,
        geometryType: a.geometryType || 'polygon',
        paths: a.paths,
        fillColor: a.fillColor,
        strokeColor: a.strokeColor || '#000000',
        area: a.area,
        areaAcres: a.areaAcres,
        perimeter: a.perimeter,
        length: a.length || '0',
        width: a.width || 0,
        radiusX: a.radiusX || 0,
        radiusY: a.radiusY || 0,
        treeDetails: a.treeDetails || undefined,
        plantDensity: a.plantDensity || DEFAULT_PLANT_DENSITY,
        modifiers: a.modifiers || DEFAULT_MODIFIERS,
        servicesRequired: a.servicesRequired || [],
        notes: a.notes || '',
        groupId: a.groupId || undefined,
        groupName: a.groupName || undefined,
      }));

      const resp = await axios.post(`${url}/api/hmi/genericPostRequest?type=SAVE_SITE_TAKEOFF`, {
        takeoffName: name,
        jobSite,
        areas: areasToSave,
        productionRates: Object.keys(rateOverrides).length > 0 ? rateOverrides : undefined,
      });

      if (resp.data?.success && resp.data?.takeoff?._id) {
        setActiveTakeoffId(resp.data.takeoff._id);
        setTakeoffName(name);
        setHasLegacyAreas(false);
        await fetchTakeoffList(jobSite._id);
        await loadTakeoffById(resp.data.takeoff._id);
        showSnackbar(`Imported ${areasToSave.length} areas as "${name}"`, 'success');
      } else {
        showSnackbar(resp.data?.message || 'Failed to import areas', 'error');
      }
    } catch (err) {
      console.error('Error importing areas:', err);
      showSnackbar('Error importing areas', 'error');
    } finally {
      setSaving(false);
    }
  };

  // =============================================
  // OVERLAY UPLOAD (S3 presigned)
  // =============================================
  const handleOverlayFileUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file || !jobSite?._id) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    try {
      showSnackbar('Uploading overlay image...', 'info');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('jobSiteId', jobSite._id);

      const resp = await axios.post(`${url}/api/customer/site-takeoff-overlay-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (!resp.data?.success || !resp.data?.objectUrl) {
        showSnackbar('Failed to upload overlay', 'error');
        return;
      }

      const { objectUrl } = resp.data;

      // Track the new URL
      setSavedOverlayUrls(prev => prev.includes(objectUrl) ? prev : [...prev, objectUrl]);
      // Add overlay to map
      addOverlayToMap(objectUrl);
      showSnackbar('Overlay uploaded', 'success');
    } catch (err) {
      console.error('Error uploading overlay:', err);
      showSnackbar('Error uploading overlay image', 'error');
    }
  }, [url, jobSite]);

  const addOverlayToMap = useCallback((imgUrl) => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const id = uuidv4();
    const center = map.getCenter();
    const bounds = map.getBounds();
    const latSpan = (bounds.getNorth() - bounds.getSouth()) * 0.25;
    const lngSpan = (bounds.getEast() - bounds.getWest()) * 0.25;

    const corners = [
      L.latLng(center.lat + latSpan, center.lng - lngSpan),
      L.latLng(center.lat + latSpan, center.lng + lngSpan),
      L.latLng(center.lat - latSpan, center.lng - lngSpan),
      L.latLng(center.lat - latSpan, center.lng + lngSpan),
    ];

    const layer = L.distortableImageOverlay(imgUrl, {
      corners,
      mode: 'rotate',
      selected: false,
      opacity: 0.6,
    }).addTo(map);

    overlayLayersRef.current[id] = layer;
    setOverlays(prev => [...prev, { id, imageUrl: imgUrl, visible: true, opacity: 0.6, category: '', subType: '' }]);
  }, []);

  // =============================================
  // HELPERS
  // =============================================
  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenGoogleStreetView = () => {
    if (!selectedSite?.latitude || !selectedSite?.longitude) return;
    const url = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${selectedSite.latitude},${selectedSite.longitude}`;
    window.open(url, '_blank');
  };

  const handleOpenMCMaps = () => {
    if (!selectedSite?.address) return;
    const addy = `${selectedSite.address}, ${selectedSite.zip || ''}`;
    const url = `https://maps.mcassessor.maricopa.gov/?search=${encodeURIComponent(addy)}`;
    window.open(url, '_blank');
  };

  // =============================================
  // PLAN OVERLAY (leaflet-distortableimage)
  // =============================================
  const handleAddOverlay = useCallback((event) => {
    if (!mapInstanceRef.current) return;
    // If there are saved overlays, show menu; otherwise just open file picker
    if (savedOverlayUrls.length > 0) {
      setOverlayMenuAnchor(event.currentTarget);
    } else {
      if (fileInputRef.current) fileInputRef.current.click();
    }
  }, [savedOverlayUrls]);

  const handleOverlayMenuClose = useCallback(() => {
    setOverlayMenuAnchor(null);
  }, []);

  const handlePickSavedOverlay = useCallback((overlayUrl) => {
    setOverlayMenuAnchor(null);
    addOverlayToMap(overlayUrl);
    showSnackbar('Overlay added from saved image', 'success');
  }, [addOverlayToMap]);

  const handleUploadNewOverlay = useCallback(() => {
    setOverlayMenuAnchor(null);
    if (fileInputRef.current) fileInputRef.current.click();
  }, []);

  const handleToggleOverlay = useCallback((ovId) => {
    setOverlays(prev => prev.map(ov => {
      if (ov.id !== ovId) return ov;
      const layer = overlayLayersRef.current[ovId];
      if (!layer) return ov;
      const nowVisible = !ov.visible;
      if (nowVisible) {
        layer.setOpacity(ov.opacity);
      } else {
        layer.deselect();
        layer.setOpacity(0);
      }
      return { ...ov, visible: nowVisible };
    }));
    // If hiding the active overlay, deactivate it
    setActiveOverlayId(prev => prev === ovId ? null : prev);
  }, []);

  const handleOverlayOpacityChange = useCallback((ovId, val) => {
    const layer = overlayLayersRef.current[ovId];
    if (layer) layer.setOpacity(val);
    setOverlays(prev => prev.map(ov => ov.id === ovId ? { ...ov, opacity: val } : ov));
  }, []);

  const handleToggleLockOverlay = useCallback((ovId) => {
    const layer = overlayLayersRef.current[ovId];
    if (!layer || !layer.editing) return;
    const isCurrentlyLocked = layer.editing._mode === 'lock';
    if (isCurrentlyLocked) {
      layer.editing._unlock();
    } else {
      layer.editing._lock();
    }
    setOverlays(prev => prev.map(ov => ov.id === ovId ? { ...ov, locked: !isCurrentlyLocked } : ov));
  }, []);

  const handleRemoveOverlay = useCallback((ovId) => {
    const map = mapInstanceRef.current;
    const layer = overlayLayersRef.current[ovId];
    if (layer) {
      try {
        layer.deselect();
        layer.remove();
      } catch (e) {
        try { if (map) map.removeLayer(layer); } catch (_) {}
      }
    }
    delete overlayLayersRef.current[ovId];
    setOverlays(prev => prev.filter(ov => ov.id !== ovId));
  }, []);

  // ===== ACTIVE OVERLAY (only active overlay gets pointer events) =====
  const handleActivateOverlay = useCallback((ovId) => {
    const newId = activeOverlayId === ovId ? null : ovId;
    setActiveOverlayId(newId);

    // Re-enable editing with correct mode on the newly activated overlay
    if (newId) {
      const ov = overlays.find(o => o.id === newId);
      const layer = overlayLayersRef.current[newId];
      if (layer && layer.editing && ov) {
        try {
          layer.editing.enable();
          if (ov.locked) {
            layer.editing._lock();
          } else {
            layer.editing._unlock();
          }
        } catch (_) {}
      }
    }
  }, [activeOverlayId, overlays]);

  // Sync pointer events and editing whenever activeOverlayId or overlays change
  useEffect(() => {
    const map = mapInstanceRef.current;
    overlays.forEach(ov => {
      const layer = overlayLayersRef.current[ov.id];
      if (!layer) return;

      const shouldBeInteractive = ov.visible && ov.id === activeOverlayId;

      if (shouldBeInteractive) {
        // Active overlay — enable editing and pointer events
        if (layer.editing && !layer.editing.enabled()) {
          try { layer.editing.enable(); } catch (_) {}
        }
        const el = layer.getElement();
        if (el) {
          el.style.pointerEvents = '';
          el.style.zIndex = '650';
        }
        // Also enable pointer events on any edit handles/toolbar
        const container = layer._image?.parentElement;
        if (container) container.style.pointerEvents = '';
      } else {
        // Inactive overlay — disable editing completely and block all events
        if (layer.editing && layer.editing.enabled()) {
          try {
            layer.deselect();
            layer.editing.disable();
          } catch (_) {}
        }
        const el = layer.getElement();
        if (el) {
          el.style.pointerEvents = 'none';
          el.style.zIndex = '';
        }
        // Also block pointer events on the layer's container (catches handles/corners)
        const container = layer._image?.parentElement;
        if (container) container.style.pointerEvents = 'none';
      }
    });
  }, [activeOverlayId, overlays]);

  const handleOverlayCategoryChange = useCallback((ovId, field, value) => {
    setOverlays(prev => prev.map(ov => {
      if (ov.id !== ovId) return ov;
      if (field === 'category') {
        return { ...ov, category: value, subType: '' };
      }
      return { ...ov, [field]: value };
    }));
  }, []);

  // ===== MEASUREMENT LABELS =====
  const rebuildMeasurementLabels = useCallback(() => {
    const group = measurementLayerRef.current;
    if (!group) return;
    group.clearLayers();

    areas.forEach(area => {
      if (!area.paths || area.paths.length < 2) return;
      if (area.geometryType === 'point' || area.geometryType === 'ellipse') return;

      const paths = area.paths;
      const isPolygon = area.geometryType !== 'polyline';
      const segments = isPolygon ? paths.length : paths.length - 1;

      for (let i = 0; i < segments; i++) {
        const p1 = paths[i];
        const p2 = paths[(i + 1) % paths.length];
        const ll1 = L.latLng(p1.lat, p1.lng);
        const ll2 = L.latLng(p2.lat, p2.lng);
        const distFeet = ll1.distanceTo(ll2) * 3.28084;

        // Skip very short segments
        if (distFeet < 1) continue;

        // Midpoint for label placement
        const midLat = (p1.lat + p2.lat) / 2;
        const midLng = (p1.lng + p2.lng) / 2;

        // Format: show feet, or feet + inches for short segments
        let label;
        if (distFeet >= 10) {
          label = `${Math.round(distFeet)}'`;
        } else {
          const feet = Math.floor(distFeet);
          const inches = Math.round((distFeet - feet) * 12);
          label = `${feet}'${inches}"`;
        }

        const marker = L.marker([midLat, midLng], {
          icon: L.divIcon({
            className: '',
            html: `<div style="
              display: inline-block;
              background: rgba(0,0,0,0.8); color: #fff; font-size: 11px; font-weight: 700;
              padding: 2px 6px; border-radius: 3px; white-space: nowrap;
              font-family: monospace; pointer-events: none;
              transform: translate(-50%, -50%);
            ">${label}</div>`,
            iconSize: [0, 0],
            iconAnchor: [0, 0],
          }),
          interactive: false,
        });
        group.addLayer(marker);
      }
    });
  }, [areas]);

  const handleToggleMeasurements = useCallback(() => {
    const map = mapInstanceRef.current;
    const group = measurementLayerRef.current;
    if (!map || !group) return;

    if (showMeasurements) {
      map.removeLayer(group);
    } else {
      rebuildMeasurementLabels();
      map.addLayer(group);
    }
    setShowMeasurements(prev => !prev);
  }, [showMeasurements, rebuildMeasurementLabels]);

  // Rebuild labels when areas change (if measurements are visible)
  useEffect(() => {
    if (showMeasurements) {
      rebuildMeasurementLabels();
    }
  }, [areas, showMeasurements, rebuildMeasurementLabels]);

  const handleToggleAreas = useCallback(() => {
    const map = mapInstanceRef.current;
    const drawnItems = drawnItemsRef.current;
    if (!map || !drawnItems) return;
    if (areasVisible) {
      map.removeLayer(drawnItems);
    } else {
      map.addLayer(drawnItems);
    }
    setAreasVisible(prev => !prev);
  }, [areasVisible]);

  const handleToggleAllOverlays = useCallback(() => {
    const anyVisible = overlays.some(ov => ov.visible);
    setOverlays(prev => prev.map(ov => {
      const layer = overlayLayersRef.current[ov.id];
      if (!layer) return ov;
      if (anyVisible) {
        layer.deselect();
        layer.setOpacity(0);
      } else {
        layer.setOpacity(ov.opacity);
      }
      return { ...ov, visible: !anyVisible };
    }));
    if (anyVisible) setActiveOverlayId(null);
  }, [overlays]);

  // =============================================
  // RENDER
  // =============================================
  return (
    <div className={classes.pageContainer}>
      {/* Header */}
      <div className={classes.header}>
        <Typography variant="h6">Site Takeoff</Typography>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {providers.length > 1 && (
            <FormControl variant="outlined" size="small" style={{ minWidth: 180 }}>
              <InputLabel>Map Layer</InputLabel>
              <Select
                label="Map Layer"
                value={currentProvider?.name || ''}
                onChange={(e) => {
                  const p = providers.find(p => p.name === e.target.value);
                  if (p) handleProviderChange(p);
                }}
              >
                {providers.map(p => (
                  <MenuItem key={p.name} value={p.name}>{p.displayName || p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {/* GIS Overlay Layers */}
          {GIS_OVERLAYS.map(ov => {
            const isActive = activeGisOverlays.includes(ov.key);
            return (
              <Tooltip key={ov.key} title={isActive ? `Hide ${ov.label}` : `Show ${ov.label}`}>
                <Chip
                  size="small"
                  label={ov.label}
                  onClick={() => handleToggleGisOverlay(ov)}
                  style={{
                    cursor: 'pointer', fontSize: '0.7rem', height: 24,
                    backgroundColor: isActive ? `${ov.color}22` : '#f5f5f5',
                    border: `1px solid ${isActive ? ov.color : '#ccc'}`,
                    color: isActive ? ov.color : undefined,
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </Tooltip>
            );
          })}
          <Divider orientation="vertical" flexItem style={{ margin: '0 2px' }} />
          {propertyLines.length > 0 && (
            <Tooltip title={showPropertyLines ? 'Hide property lines' : 'Show property lines'}>
              <Chip
                icon={showPropertyLines ? <Visibility style={{ fontSize: 16 }} /> : <VisibilityOff style={{ fontSize: 16 }} />}
                label={`${propertyLines.length} Parcels`}
                size="small"
                onClick={() => setShowPropertyLines(prev => !prev)}
                style={{
                  backgroundColor: showPropertyLines ? '#e8f5e9' : '#f5f5f5',
                  border: showPropertyLines ? '1px solid #4caf50' : '1px solid #ccc',
                  cursor: 'pointer',
                }}
              />
            </Tooltip>
          )}
          <Chip label={`${areas.length} Areas`} size="small" style={{ backgroundColor: '#accacd' }} />
          <Tooltip title={areasVisible ? 'Hide areas' : 'Show areas'}>
            <IconButton size="small" onClick={handleToggleAreas} style={{ padding: 2 }}>
              {areasVisible
                ? <Visibility style={{ fontSize: 16, color: '#4caf50' }} />
                : <VisibilityOff style={{ fontSize: 16, color: '#999' }} />
              }
            </IconButton>
          </Tooltip>
          <Tooltip title={showMeasurements ? 'Hide measurements' : 'Show segment measurements'}>
            <Chip
              label="Dims"
              size="small"
              onClick={handleToggleMeasurements}
              style={{
                cursor: 'pointer', height: 22, fontSize: '0.68rem',
                backgroundColor: showMeasurements ? '#1565c0' : '#f0f0f0',
                color: showMeasurements ? '#fff' : '#666',
                border: showMeasurements ? '1px solid #1565c0' : '1px solid #ccc',
              }}
            />
          </Tooltip>
          <Divider orientation="vertical" flexItem style={{ margin: '0 4px' }} />
          <Tooltip title="Add plan overlay image">
            <Chip
              icon={<LayersIcon style={{ fontSize: 16 }} />}
              label={overlays.length ? `Overlays (${overlays.length})` : 'Plan Overlay'}
              size="small"
              onClick={handleAddOverlay}
              style={{ cursor: 'pointer', border: '1px solid #ccc', backgroundColor: overlays.length ? '#fff3e0' : undefined }}
            />
          </Tooltip>
          {overlays.length > 0 && (
            <Tooltip title={overlays.some(o => o.visible) ? 'Hide all overlays' : 'Show all overlays'}>
              <IconButton size="small" onClick={handleToggleAllOverlays} style={{ padding: 2 }}>
                {overlays.some(o => o.visible)
                  ? <Visibility style={{ fontSize: 16 }} />
                  : <VisibilityOff style={{ fontSize: 16 }} />
                }
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className={classes.contentRow}>
        {/* Map */}
        <div className={classes.mapContainer}>
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />
          {/* Transparent click-capture layer — sits above ALL map layers during placement mode */}
          {/* Hold Space to pause (hides overlay, lets you pan/zoom) */}
          {placingItemId && !placementPaused && (
            <div
              ref={placementOverlayRef}
              onClick={handlePlacementClick}
              onWheel={(e) => {
                // Pass scroll-wheel through to the map for zooming
                const map = mapInstanceRef.current;
                if (map) {
                  const delta = e.deltaY > 0 ? -1 : 1;
                  map.zoomIn(delta, { animate: true });
                }
              }}
              style={{
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                zIndex: 10000, cursor: 'crosshair',
              }}
            />
          )}
          {isDrawing && (
            <div className={classes.drawingBanner}>
              Click to place points, double-click to finish
            </div>
          )}
          {editingAreaId && (
            <div className={classes.drawingBanner} style={{ backgroundColor: '#1565c0' }}>
              Editing shape — drag vertices to adjust
            </div>
          )}
          {placingItemId && (
            <div className={classes.drawingBanner} style={{ backgroundColor: placementPaused ? '#555' : '#e65100' }}>
              {placementPaused
                ? 'Paused — release Space to resume placing'
                : `Click to place: ${placementQueue.find(q => q.queueId === placingItemId)?.label || 'item'} — hold Space to pan/zoom`
              }
              <span
                style={{ marginLeft: 12, cursor: 'pointer', textDecoration: 'underline', fontSize: '0.8rem' }}
                onClick={() => setPlacingItemId(null)}
              >Cancel</span>
            </div>
          )}
        </div>

        {/* Sidebar — stop keyboard events from reaching the Leaflet map/overlay */}
        <Paper className={classes.sidebar} elevation={1} onKeyDown={e => e.stopPropagation()} onKeyUp={e => e.stopPropagation()} onKeyPress={e => e.stopPropagation()}>
          {/* Site Selector */}
          <div className={classes.siteSelector}>
            <Autocomplete
              id="st-customer"
              options={clients || []}
              getOptionLabel={(opt) => opt?.displayName || opt?.searchName || ''}
              className={classes.paddedField}
              onChange={handleClientChange}
              fullWidth
              size="small"
              value={selectedClient}
              renderInput={(params) => <TextField variant="outlined" {...params} label="Customer" />}
            />
            <Autocomplete
              id="st-jobsite"
              options={availableSites || []}
              getOptionLabel={(site) => `${site?.name || ''} ${site?.address || ''}`}
              className={classes.paddedField}
              onChange={handleSiteChange}
              fullWidth
              size="small"
              value={selectedSite}
              renderInput={(params) => <TextField variant="outlined" {...params} label="Jobsite" />}
            />
            {selectedSite?._id && (
              <div className={classes.siteInfo}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" noWrap style={{ maxWidth: '70%' }}>
                    {selectedSite.name} {selectedSite.address}
                  </Typography>
                  <div>
                    <Tooltip title="MC Assessor Maps">
                      <IconButton size="small" onClick={handleOpenMCMaps}><Search /></IconButton>
                    </Tooltip>
                    <Tooltip title="Google Street View">
                      <IconButton size="small" onClick={handleOpenGoogleStreetView}><Streetview /></IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Takeoff Selector */}
          {selectedSite?._id && (
            <Box px={1} py={0.5} style={{ borderBottom: '1px solid #eee', backgroundColor: '#f0f7ff' }}>
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <FormControl variant="outlined" size="small" style={{ flex: 1, minWidth: 0 }}>
                  <InputLabel>Takeoff</InputLabel>
                  <Select
                    label="Takeoff"
                    value={activeTakeoffId || ''}
                    onChange={(e) => handleSwitchTakeoff(e.target.value)}
                    displayEmpty
                  >
                    {!activeTakeoffId && (
                      <MenuItem value="" disabled>
                        <em>{takeoffs.length ? 'Select a takeoff' : 'No takeoffs yet'}</em>
                      </MenuItem>
                    )}
                    {takeoffs.map(t => (
                      <MenuItem key={t._id} value={t._id}>
                        {t.name} ({t.areaCount} areas)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Tooltip title="New Takeoff">
                  <IconButton size="small" onClick={handleNewTakeoff} style={{ border: '1px solid #ccc', borderRadius: 4 }}>
                    <Add fontSize="small" />
                  </IconButton>
                </Tooltip>
                {activeTakeoffId && (
                  <Tooltip title="Delete Takeoff">
                    <IconButton size="small" onClick={handleDeleteTakeoff} style={{ border: '1px solid #ccc', borderRadius: 4 }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
              {/* Editable takeoff name */}
              {(activeTakeoffId || takeoffName) && (
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  value={takeoffName}
                  onChange={(e) => setTakeoffName(e.target.value)}
                  placeholder="Takeoff name"
                  style={{ marginTop: 4 }}
                  inputProps={{ style: { fontSize: '0.82rem', padding: '6px 8px' } }}
                />
              )}
              {/* Import existing areas button */}
              {hasLegacyAreas && takeoffs.length === 0 && areas.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ImportIcon />}
                  onClick={handleImportLegacyAreas}
                  disabled={saving}
                  fullWidth
                  style={{ marginTop: 4, fontSize: '0.75rem', textTransform: 'none' }}
                >
                  Import {areas.length} Existing Areas as Takeoff
                </Button>
              )}
            </Box>
          )}

          {/* Overlay controls */}
          {overlays.length > 0 && (
            <Box px={1} py={0.5} style={{ borderBottom: '1px solid #eee', backgroundColor: '#fff8e1' }}>
              <Typography variant="caption" style={{ fontWeight: 600, fontSize: '0.72rem', color: '#e65100' }}>
                Plan Overlays ({overlays.length})
              </Typography>
              {overlays.map((ov, idx) => {
                const fileName = (ov.imageUrl || '').split('/').pop()?.replace(/^\d+_/, '') || `Overlay ${idx + 1}`;
                const subTypes = ov.category ? (OVERLAY_SUB_TYPES[ov.category] || []) : [];
                const isActive = activeOverlayId === ov.id;
                return (
                  <Box key={ov.id} style={{
                    padding: '4px 0',
                    borderBottom: idx < overlays.length - 1 ? '1px solid #f0f0f0' : 'none',
                    backgroundColor: isActive ? '#fff3e0' : 'transparent',
                    borderLeft: isActive ? '3px solid #ff9800' : '3px solid transparent',
                    paddingLeft: 4,
                  }}>
                    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                      <Tooltip title={ov.visible ? 'Hide' : 'Show'}>
                        <IconButton size="small" onClick={() => handleToggleOverlay(ov.id)} style={{ padding: 2 }}>
                          {ov.visible
                            ? <Visibility style={{ fontSize: 14, color: '#ff9800' }} />
                            : <VisibilityOff style={{ fontSize: 14, color: '#999' }} />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={isActive ? 'Click to deactivate' : 'Click to activate (only active overlay can be moved)'}>
                        <Typography
                          variant="caption"
                          onClick={() => handleActivateOverlay(ov.id)}
                          style={{
                            flex: 1, fontSize: '0.72rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            fontWeight: isActive ? 700 : 400,
                            color: isActive ? '#e65100' : 'inherit',
                          }}
                        >
                          {isActive ? '\u25B6 ' : ''}{fileName.length > 25 ? fileName.substring(0, 25) + '...' : fileName}
                        </Typography>
                      </Tooltip>
                      <Tooltip title={ov.locked ? 'Unlock overlay' : 'Lock overlay'}>
                        <IconButton size="small" onClick={() => handleToggleLockOverlay(ov.id)} style={{ padding: 2 }}>
                          {ov.locked
                            ? <LockIcon style={{ fontSize: 14, color: '#d32f2f' }} />
                            : <LockOpenIcon style={{ fontSize: 14, color: '#999' }} />
                          }
                        </IconButton>
                      </Tooltip>
                      <Slider
                        value={ov.opacity}
                        onChange={(e, val) => handleOverlayOpacityChange(ov.id, val)}
                        min={0} max={1} step={0.05}
                        size="small"
                        style={{ width: 50, color: '#ff9800', margin: '0 4px' }}
                      />
                      <Tooltip title="Remove">
                        <IconButton size="small" onClick={() => handleRemoveOverlay(ov.id)} style={{ padding: 2 }}>
                          <Delete style={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box display="flex" style={{ gap: 4, paddingLeft: 28, paddingTop: 2 }}>
                      <Select
                        value={ov.category || ''}
                        onChange={(e) => handleOverlayCategoryChange(ov.id, 'category', e.target.value)}
                        displayEmpty
                        style={{ fontSize: '0.7rem', height: 24, flex: 1 }}
                      >
                        {OVERLAY_CATEGORIES.map(c => (
                          <MenuItem key={c.value} value={c.value} style={{ fontSize: '0.75rem' }}>{c.label}</MenuItem>
                        ))}
                      </Select>
                      {subTypes.length > 0 && (
                        <Select
                          value={ov.subType || ''}
                          onChange={(e) => handleOverlayCategoryChange(ov.id, 'subType', e.target.value)}
                          displayEmpty
                          style={{ fontSize: '0.7rem', height: 24, flex: 1 }}
                        >
                          {subTypes.map(s => (
                            <MenuItem key={s.value} value={s.value} style={{ fontSize: '0.75rem' }}>{s.label}</MenuItem>
                          ))}
                        </Select>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}

          {/* Tabs */}
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Areas" />
            <Tab label="Takeoff" />
            <Tab label={placementQueue.length ? `Queue (${placementQueue.filter(q => q.status === 'unplaced').length})` : 'Queue'} />
          </Tabs>

          {/* Tab Content */}
          <div className={classes.sidebarScroll}>
            {activeTab === 0 && (
              <>
                {/* Merge / Group toolbar */}
                {areas.length >= 2 && (
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" px={1} py={0.5}
                    style={{
                      borderBottom: (mergeMode || groupMode) ? `2px solid ${mergeMode ? '#ff9800' : '#7b1fa2'}` : 'none',
                      backgroundColor: mergeMode ? '#fff3e0' : groupMode ? '#f3e5f5' : 'transparent',
                    }}
                  >
                    <Box display="flex" gap={0.5} style={{ gap: 4 }}>
                      <Button
                        size="small"
                        variant={mergeMode ? 'contained' : 'outlined'}
                        startIcon={<MergeIcon />}
                        disabled={groupMode}
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: mergeMode ? '#ff9800' : undefined, color: mergeMode ? '#fff' : undefined }}
                        onClick={() => {
                          if (mergeMode) { setMergeMode(false); setMergeSelection([]); }
                          else { setMergeMode(true); setMergeSelection([]); }
                        }}
                      >
                        {mergeMode ? 'Cancel' : 'Merge'}
                      </Button>
                      <Button
                        size="small"
                        variant={groupMode ? 'contained' : 'outlined'}
                        startIcon={<GroupIcon />}
                        disabled={mergeMode}
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: groupMode ? '#7b1fa2' : undefined, color: groupMode ? '#fff' : undefined }}
                        onClick={() => {
                          if (groupMode) { setGroupMode(false); setGroupSelection([]); }
                          else { setGroupMode(true); setGroupSelection([]); }
                        }}
                      >
                        {groupMode ? 'Cancel' : 'Group'}
                      </Button>
                    </Box>
                    {mergeMode && mergeSelection.length >= 2 && (
                      <Button size="small" variant="contained" color="primary"
                        style={{ fontSize: '0.72rem', textTransform: 'none' }} onClick={handleMergeAreas}
                      >Merge {mergeSelection.length}</Button>
                    )}
                    {mergeMode && mergeSelection.length < 2 && (
                      <Typography variant="caption" color="textSecondary">Select {2 - mergeSelection.length} more</Typography>
                    )}
                    {groupMode && groupSelection.length >= 2 && (
                      <Button size="small" variant="contained"
                        style={{ fontSize: '0.72rem', textTransform: 'none', backgroundColor: '#7b1fa2', color: '#fff' }}
                        onClick={handleCreateGroup}
                      >Group {groupSelection.length}</Button>
                    )}
                    {groupMode && groupSelection.length < 2 && (
                      <Typography variant="caption" color="textSecondary">Select {2 - groupSelection.length} more</Typography>
                    )}
                  </Box>
                )}

                {/* Area List — organized by groups */}
                {(() => {
                  const selectionMode = mergeMode || groupMode;
                  // Separate grouped vs ungrouped
                  const groups = {};
                  const ungrouped = [];
                  areas.forEach(area => {
                    if (area.groupId) {
                      if (!groups[area.groupId]) groups[area.groupId] = { name: area.groupName || 'Unnamed Group', areas: [] };
                      groups[area.groupId].areas.push(area);
                    } else {
                      ungrouped.push(area);
                    }
                  });

                  const renderAreaItem = (area) => {
                    const aId = area._id || area.id;
                    const isSelected = aId === selectedAreaId;
                    const isMergeSelected = mergeSelection.includes(aId);
                    const isGroupSelected = groupSelection.includes(aId);
                    const isChecked = isMergeSelected || isGroupSelected;
                    const color = getAreaTypeColor(area.areaType);
                    const highlightStyle = isMergeSelected
                      ? { border: '2px solid #ff9800', backgroundColor: 'rgba(255,152,0,0.06)' }
                      : isGroupSelected
                        ? { border: '2px solid #7b1fa2', backgroundColor: 'rgba(123,31,162,0.06)' }
                        : undefined;

                    return (
                      <div
                        key={aId}
                        className={`${classes.areaListItem} ${isSelected && !selectionMode ? classes.areaListItemSelected : ''}`}
                        style={highlightStyle}
                        onClick={() => {
                          if (mergeMode) handleToggleMergeSelect(aId);
                          else if (groupMode) handleToggleGroupSelect(aId);
                          else handleSelectArea(aId);
                        }}
                      >
                        {selectionMode && (
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            style={{ padding: 2, marginRight: 4 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (mergeMode) handleToggleMergeSelect(aId);
                              else handleToggleGroupSelect(aId);
                            }}
                          />
                        )}
                        <div className={classes.colorDot} style={{ backgroundColor: color }} />
                        <div className={classes.areaItemInfo}>
                          <div className={classes.areaItemName}>
                            {area.name || getAreaTypeLabel(area.areaType)}
                            {area.isNew && (
                              <Chip size="small" label="new" style={{ height: 16, fontSize: '0.6rem', marginLeft: 4, backgroundColor: '#ff9800', color: '#fff' }} />
                            )}
                          </div>
                          <div className={classes.areaItemMeta}>
                            {getAreaTypeLabel(area.areaType)} | {
                              area.geometryType === 'point'
                                ? (area.treeDetails?.species || area.treeDetails?.condition
                                  ? [area.treeDetails.species, area.treeDetails.condition].filter(Boolean).join(' — ')
                                  : 'Point')
                              : area.geometryType === 'ellipse' ? `${parseFloat(area.radiusX || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} ft radius | ${parseFloat(area.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF`
                              : area.geometryType === 'polyline' ? `${parseFloat(area.length || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} LF x ${area.width || 0} ft = ${parseFloat(area.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF`
                              : `${parseFloat(area.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF`
                            }
                          </div>
                        </div>
                        {!selectionMode && (
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleDeleteArea(aId); }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>
                    );
                  };

                  return (
                    <>
                      {/* Grouped areas */}
                      {Object.entries(groups).map(([gid, group]) => {
                        const groupSF = group.areas.reduce((s, a) => s + (parseFloat(a.area) || 0), 0);
                        return (
                          <Box key={gid} mb={0.5}>
                            <Box display="flex" alignItems="center" px={1} py={0.5}
                              style={{ backgroundColor: '#f3e5f5', borderRadius: 4, borderLeft: '3px solid #7b1fa2' }}
                            >
                              <GroupIcon style={{ fontSize: 16, color: '#7b1fa2', marginRight: 6 }} />
                              <Typography style={{ fontSize: '0.78rem', fontWeight: 600, flex: 1, color: '#4a148c' }}>
                                {group.name}
                              </Typography>
                              <Typography style={{ fontSize: '0.7rem', color: '#7b1fa2', marginRight: 4 }}>
                                {group.areas.length} areas | {groupSF.toLocaleString('en-US', { maximumFractionDigits: 0 })} SF
                              </Typography>
                              {!selectionMode && (
                                <Tooltip title="Ungroup all">
                                  <IconButton size="small" onClick={() => handleUngroupAll(gid)}>
                                    <UngroupIcon style={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                            <Box pl={1.5}>
                              {group.areas.map(renderAreaItem)}
                            </Box>
                          </Box>
                        );
                      })}

                      {/* Ungrouped areas */}
                      {ungrouped.map(renderAreaItem)}
                    </>
                  );
                })()}

                {areas.length === 0 && (
                  <Typography variant="body2" color="textSecondary" style={{ padding: 16, textAlign: 'center' }}>
                    Use the drawing tools on the map to create areas.
                  </Typography>
                )}

                {/* Editor for selected area */}
                {selectedArea && (() => {
                  const svcCount = (selectedArea.servicesRequired || []).length;
                  const mods = selectedArea.modifiers || {};
                  const modCount = [
                    mods.access === 'obstructed', mods.slope === 'sloped',
                    mods.litterExposure === 'high', mods.weedPressure !== 'low' && mods.weedPressure,
                    mods.limitedAccess, mods.blowRestrictions,
                    mods.irrigationIncluded !== 'none' && mods.irrigationIncluded,
                  ].filter(Boolean).length;

                  return (
                  <div className={classes.editorSection}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Typography variant="subtitle2">
                        Edit: {selectedArea.name || getAreaTypeLabel(selectedArea.areaType)}
                      </Typography>
                      {editingAreaId === (selectedArea._id || selectedArea.id) ? (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            style={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, padding: '2px 10px' }}
                            onClick={() => handleFinishEditShape(selectedArea._id || selectedArea.id)}
                          >
                            Done
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none', minWidth: 0, padding: '2px 10px' }}
                            onClick={handleCancelEditShape}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Tooltip title="Edit polygon shape on map">
                          <IconButton
                            size="small"
                            onClick={() => handleStartEditShape(selectedArea._id || selectedArea.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>

                    {editingAreaId === (selectedArea._id || selectedArea.id) && (
                      <Box p={1} mb={1} bgcolor="#e3f2fd" borderRadius={4} style={{ fontSize: '0.78rem', color: '#1565c0' }}>
                        Drag vertices to reshape this area. Click Done when finished.
                      </Box>
                    )}

                    {/* Editor Tabs */}
                    <Tabs
                      value={editorTab}
                      onChange={(e, v) => setEditorTab(v)}
                      className={classes.editorTabs}
                      indicatorColor="primary"
                      textColor="primary"
                      variant="fullWidth"
                    >
                      <Tab label="Details" />
                      <Tab label={svcCount ? `Services (${svcCount})` : 'Services'} />
                    </Tabs>

                    {/* ===== DETAILS TAB ===== */}
                    {editorTab === 0 && (
                      <Box pt={1}>
                        <TextField
                          label="Area Name"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={selectedArea.name || ''}
                          onChange={(e) => handleUpdateArea(selectedAreaId, 'name', e.target.value)}
                          style={{ marginBottom: 8 }}
                        />

                        {/* Group badge */}
                        {selectedArea.groupId && (
                          <Box display="flex" alignItems="center" mb={1} px={1} py={0.5}
                            style={{ backgroundColor: '#f3e5f5', borderRadius: 4, borderLeft: '3px solid #7b1fa2' }}
                          >
                            <GroupIcon style={{ fontSize: 14, color: '#7b1fa2', marginRight: 6 }} />
                            <Typography style={{ fontSize: '0.75rem', flex: 1, color: '#4a148c' }}>
                              {selectedArea.groupName}
                            </Typography>
                            <Tooltip title="Remove from group">
                              <IconButton size="small" onClick={() => handleUngroupArea(selectedAreaId)}>
                                <UngroupIcon style={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}

                        <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 8 }}>
                          <InputLabel>Area Type</InputLabel>
                          <Select
                            label="Area Type"
                            value={selectedArea.areaType || AREA_TYPES.GR_CLEAN}
                            onChange={(e) => handleUpdateArea(selectedAreaId, 'areaType', e.target.value)}
                          >
                            {Object.entries(AREA_CATEGORIES).map(([catKey, cat]) => [
                              <MenuItem key={`header-${catKey}`} disabled style={{ opacity: 1, fontWeight: 600, fontSize: '0.75rem', backgroundColor: '#f5f5f5' }}>
                                {cat.label}
                              </MenuItem>,
                              ...cat.types.map(t => (
                                <MenuItem key={t} value={t}>
                                  <span style={{
                                    width: 10, height: 10, borderRadius: '50%',
                                    backgroundColor: AREA_TYPE_COLORS[t],
                                    display: 'inline-block', marginRight: 8,
                                    border: '1px solid rgba(0,0,0,0.15)',
                                  }} />
                                  {AREA_TYPE_LABELS[t]}
                                </MenuItem>
                              ))
                            ])}
                          </Select>
                        </FormControl>

                        {/* Geometry info */}
                        <Box p={1} mb={1} bgcolor="#f0f0f0" borderRadius={4} style={{ fontSize: '0.78rem' }}>
                          {selectedArea.geometryType === 'point' ? (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Type:</span>
                                <strong>Point Marker</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Location:</span>
                                <strong>{selectedArea.paths?.[0]?.lat?.toFixed(6)}, {selectedArea.paths?.[0]?.lng?.toFixed(6)}</strong>
                              </div>
                            </>
                          ) : selectedArea.geometryType === 'ellipse' ? (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Radius:</span>
                                <strong>{parseFloat(selectedArea.radiusX || 0).toLocaleString('en-US', { maximumFractionDigits: 1 })} ft</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Area:</span>
                                <strong>{parseFloat(selectedArea.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Circumference:</span>
                                <strong>{parseFloat(selectedArea.perimeter || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} LF</strong>
                              </div>
                            </>
                          ) : selectedArea.geometryType === 'polyline' ? (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Length:</span>
                                <strong>{parseFloat(selectedArea.length || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} LF</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>Width:</span>
                                <TextField
                                  variant="outlined"
                                  size="small"
                                  type="number"
                                  value={selectedArea.width ?? DEFAULT_LINE_WIDTH}
                                  onChange={(e) => {
                                    const w = Math.max(0, parseFloat(e.target.value) || 0);
                                    const len = parseFloat(selectedArea.length) || 0;
                                    const areaSF = len * w;
                                    setAreas(prev => prev.map(a => {
                                      if ((a._id || a.id) !== selectedAreaId) return a;
                                      return { ...a, width: w, area: areaSF.toFixed(2), areaAcres: (areaSF / 43560).toFixed(4) };
                                    }));
                                  }}
                                  inputProps={{ min: 0, step: 0.5, style: { textAlign: 'right', padding: '4px 8px' } }}
                                  style={{ width: 80 }}
                                />
                                <span style={{ marginLeft: 4 }}>ft</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                                <span>Area:</span>
                                <strong>{parseFloat(selectedArea.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF</strong>
                              </div>
                            </>
                          ) : (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Area:</span>
                                <strong>{parseFloat(selectedArea.area || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} SF</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Perimeter:</span>
                                <strong>{parseFloat(selectedArea.perimeter || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} LF</strong>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span>Acres:</span>
                                <strong>{parseFloat(selectedArea.areaAcres || 0).toFixed(3)}</strong>
                              </div>
                            </>
                          )}
                        </Box>

                        {/* Tree Details — only for tree disposition types */}
                        {TREE_TYPES.includes(selectedArea.areaType) && (
                          <Box mb={1}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>Tree Details</Typography>
                            <Autocomplete
                              freeSolo
                              size="small"
                              options={COMMON_TREE_SPECIES}
                              value={selectedArea.treeDetails?.species || ''}
                              onInputChange={(e, val) => handleUpdateArea(selectedAreaId, 'treeDetails', { species: val })}
                              renderInput={(params) => (
                                <TextField {...params} label="Species" variant="outlined" size="small" style={{ marginBottom: 6, marginTop: 4 }} />
                              )}
                            />
                            <Box display="flex" style={{ gap: 6, marginBottom: 6 }}>
                              <TextField
                                label="Height (ft)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={selectedArea.treeDetails?.height || ''}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'treeDetails', { height: Math.max(0, parseFloat(e.target.value) || 0) })}
                                inputProps={{ min: 0, step: 1 }}
                                style={{ flex: 1 }}
                              />
                              <TextField
                                label="Caliper (in)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={selectedArea.treeDetails?.caliper || ''}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'treeDetails', { caliper: Math.max(0, parseFloat(e.target.value) || 0) })}
                                inputProps={{ min: 0, step: 0.5 }}
                                style={{ flex: 1 }}
                              />
                            </Box>
                            <Box display="flex" style={{ gap: 6, marginBottom: 6 }}>
                              <TextField
                                label="Canopy (ft)"
                                variant="outlined"
                                size="small"
                                type="number"
                                value={selectedArea.treeDetails?.canopySpread || ''}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'treeDetails', { canopySpread: Math.max(0, parseFloat(e.target.value) || 0) })}
                                inputProps={{ min: 0, step: 1 }}
                                style={{ flex: 1 }}
                              />
                              <FormControl variant="outlined" size="small" style={{ flex: 1 }}>
                                <InputLabel>Condition</InputLabel>
                                <Select
                                  label="Condition"
                                  value={selectedArea.treeDetails?.condition || ''}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'treeDetails', { condition: e.target.value })}
                                >
                                  {TREE_CONDITION_OPTIONS.map(o => (
                                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                          </Box>
                        )}

                        {/* Plant Density — only for plant-bearing types */}
                        {PLANT_BEARING_TYPES.includes(resolveAreaType(selectedArea.areaType)) && (
                          <Box mb={1}>
                            <Typography variant="caption" style={{ fontWeight: 600 }}>Plant Density</Typography>
                            <TextField
                              label="Tree Count"
                              variant="outlined"
                              size="small"
                              type="number"
                              fullWidth
                              value={selectedArea.plantDensity?.treeCount || 0}
                              onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                treeCount: Math.max(0, parseInt(e.target.value) || 0)
                              })}
                              inputProps={{ min: 0 }}
                              style={{ marginBottom: 4, marginTop: 4 }}
                            />
                            <FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 4 }}>
                              <InputLabel>Shrub Density</InputLabel>
                              <Select
                                label="Shrub Density"
                                value={selectedArea.plantDensity?.shrubDensity || 'none'}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                  shrubDensity: e.target.value
                                })}
                              >
                                {SHRUB_DENSITY_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <FormControl variant="outlined" size="small" fullWidth>
                              <InputLabel>Groundcover Density</InputLabel>
                              <Select
                                label="Groundcover Density"
                                value={selectedArea.plantDensity?.groundcoverDensity || 'none'}
                                onChange={(e) => handleUpdateArea(selectedAreaId, 'plantDensity', {
                                  groundcoverDensity: e.target.value
                                })}
                              >
                                {SHRUB_DENSITY_OPTIONS.map(o => (
                                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        )}

                        {/* Notes */}
                        <TextField
                          label="Notes"
                          variant="outlined"
                          size="small"
                          fullWidth
                          multiline
                          rows={2}
                          value={selectedArea.notes || ''}
                          onChange={(e) => handleUpdateArea(selectedAreaId, 'notes', e.target.value)}
                          style={{ marginBottom: 8 }}
                        />

                        {/* Modifiers — collapsible */}
                        <Box mb={1}>
                          <div
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '4px 0' }}
                            onClick={() => setModifiersExpanded(!modifiersExpanded)}
                          >
                            <Typography variant="caption" style={{ fontWeight: 600, flex: 1 }}>
                              Modifiers{modCount > 0 ? ` (${modCount} active)` : ''}
                            </Typography>
                            {modifiersExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                          </div>

                          <Collapse in={modifiersExpanded}>
                            <Box pl={1}>
                              <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 4 }}>
                                Site conditions that affect production rates.
                              </Typography>
                              {/* Access */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Access</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.access || 'open'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { access: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="open">Open</MenuItem>
                                  <MenuItem value="obstructed">Obstructed</MenuItem>
                                </Select>
                              </div>
                              {/* Slope */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Slope</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.slope || 'flat'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { slope: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="flat">Flat</MenuItem>
                                  <MenuItem value="sloped">Sloped</MenuItem>
                                </Select>
                              </div>
                              {/* Litter Exposure */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Litter Exposure</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.litterExposure || 'low'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { litterExposure: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="low">Low</MenuItem>
                                  <MenuItem value="high">High</MenuItem>
                                </Select>
                              </div>
                              {/* Weed Pressure */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Weed Pressure</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.weedPressure || 'low'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { weedPressure: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="low">Low</MenuItem>
                                  <MenuItem value="med">Med</MenuItem>
                                  <MenuItem value="high">High</MenuItem>
                                </Select>
                              </div>
                              {/* Limited Access */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Limited Access</span>
                                <Checkbox
                                  size="small"
                                  checked={selectedArea.modifiers?.limitedAccess || false}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { limitedAccess: e.target.checked })}
                                />
                              </div>
                              {/* Blow Restrictions */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Blow Restrictions</span>
                                <Checkbox
                                  size="small"
                                  checked={selectedArea.modifiers?.blowRestrictions || false}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { blowRestrictions: e.target.checked })}
                                />
                              </div>
                              {/* Irrigation */}
                              <div className={classes.modifierRow}>
                                <span className={classes.modifierLabel}>Irrigation</span>
                                <Select
                                  size="small"
                                  value={selectedArea.modifiers?.irrigationIncluded || 'none'}
                                  onChange={(e) => handleUpdateArea(selectedAreaId, 'modifiers', { irrigationIncluded: e.target.value })}
                                  style={{ fontSize: '0.78rem' }}
                                >
                                  <MenuItem value="none">None</MenuItem>
                                  <MenuItem value="basic">Basic</MenuItem>
                                  <MenuItem value="full">Full</MenuItem>
                                </Select>
                              </div>
                            </Box>
                          </Collapse>
                        </Box>
                      </Box>
                    )}

                    {/* ===== SERVICES TAB ===== */}
                    {editorTab === 1 && (
                      <Box pt={1}>
                        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 6 }}>
                          Click to toggle services for this area. Defaults are auto-suggested based on area type.
                        </Typography>

                        <div className={classes.servicesWrap}>
                          {Object.entries(SERVICES).map(([key, svcValue]) => {
                            const isActive = (selectedArea.servicesRequired || []).includes(svcValue);
                            const isSuggested = getSuggestedServices(selectedArea.areaType).includes(svcValue);
                            return (
                              <Chip
                                key={svcValue}
                                label={SERVICE_LABELS[svcValue]}
                                size="small"
                                className={isActive ? classes.serviceChipActive : classes.serviceChip}
                                variant={isActive ? 'default' : 'outlined'}
                                onClick={() => {
                                  const current = selectedArea.servicesRequired || [];
                                  const next = isActive
                                    ? current.filter(s => s !== svcValue)
                                    : [...current, svcValue];
                                  handleUpdateArea(selectedAreaId, 'servicesRequired', next);
                                }}
                              />
                            );
                          })}
                        </div>

                        {/* Quick actions */}
                        <Box mt={1} display="flex" style={{ gap: 8 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none' }}
                            onClick={() => {
                              const suggested = getSuggestedServices(selectedArea.areaType);
                              handleUpdateArea(selectedAreaId, 'servicesRequired', suggested);
                            }}
                          >
                            Reset to Defaults
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem', textTransform: 'none' }}
                            onClick={() => handleUpdateArea(selectedAreaId, 'servicesRequired', [])}
                          >
                            Clear All
                          </Button>
                        </Box>
                      </Box>
                    )}

                  </div>
                  );
                })()}
              </>
            )}

            {activeTab === 1 && (
              <>
                <SiteTakeoffSummary
                  areas={areas}
                  onSelectArea={(a) => {
                    setSelectedAreaId(a._id || a.id);
                    setActiveTab(0);
                  }}
                />
                <LaborEstimate areas={areas} rateOverrides={rateOverrides} />
                <Divider style={{ margin: '12px 0' }} />
                <ProductionRateEditor
                  areas={areas}
                  rateOverrides={rateOverrides}
                  onChange={setRateOverrides}
                />
              </>
            )}

            {activeTab === 2 && (
              <PlacementQueue
                queue={placementQueue}
                onQueueChange={setPlacementQueue}
                onStartPlacing={setPlacingItemId}
                placingItemId={placingItemId}
                plantSizes={plantSizes}
              />
            )}
          </div>

          {/* Save Bar */}
          <div className={classes.saveBar}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSave}
              disabled={saving || !jobSite?._id || (areas.length === 0 && overlays.length === 0)}
              fullWidth
              size="small"
            >
              {saving ? 'Saving...' : activeTakeoffId ? `Save "${takeoffName || 'Takeoff'}"` : 'Save as New Takeoff'}
            </Button>
          </div>
        </Paper>
      </div>

      {/* Overlay picker menu */}
      <Menu
        anchorEl={overlayMenuAnchor}
        open={Boolean(overlayMenuAnchor)}
        onClose={handleOverlayMenuClose}
        PaperProps={{ style: { maxWidth: 320, maxHeight: 400 } }}
      >
        <MenuItem onClick={handleUploadNewOverlay}>
          <ListItemIcon><UploadIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Upload New Image" />
        </MenuItem>
        {savedOverlayUrls.length > 0 && <Divider />}
        {savedOverlayUrls.length > 0 && (
          <MenuItem disabled style={{ opacity: 1 }}>
            <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
              Previously Used
            </Typography>
          </MenuItem>
        )}
        {savedOverlayUrls.map((overlayUrl, idx) => {
          const fileName = overlayUrl.split('/').pop() || `Overlay ${idx + 1}`;
          // Strip timestamp prefix for display
          const displayName = fileName.replace(/^\d+_/, '');
          return (
            <MenuItem key={overlayUrl} onClick={() => handlePickSavedOverlay(overlayUrl)}>
              <ListItemIcon>
                <img
                  src={overlayUrl}
                  alt=""
                  style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                />
              </ListItemIcon>
              <ListItemText
                primary={displayName.length > 30 ? displayName.substring(0, 30) + '...' : displayName}
                primaryTypographyProps={{ style: { fontSize: '0.82rem' } }}
              />
            </MenuItem>
          );
        })}
      </Menu>

      {/* Hidden file input for overlay upload */}
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleOverlayFileUpload}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.type}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

const mapStateToProps = (state) => ({
  url: state.url,
  user: state.user,
  notification: state.notification,
  clients: state.clients,
});

export default connect(mapStateToProps)(React.memo(SiteTakeoffPage));
